import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { Layout } from '@/components/layout';
import { useAuthStore } from '@/lib/auth-store';
import { useToast } from '@/hooks/use-toast';
import {
  Car, Search, Wand2, Check, Loader2, ChevronDown, ChevronUp, ChevronRight,
  RefreshCw, CheckCircle2, XCircle, Settings2, X, ExternalLink, Pencil,
  Copy, ClipboardPaste, GitMerge, FileCode, Save, Monitor, Eye, EyeOff,
  Download, Upload, AlertTriangle, Palette, Type, Trash2, Plus, Globe,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ShopifyTargetStoreSelector } from '@/components/shopify-target-store';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { validFitmentYear, planAddFitmentYear, confirmedFitmentIds } from '@/lib/fitment-add-year';

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

// ── Types ─────────────────────────────────────────────────────────────────────

interface ShopifyStore { id: string; nickname: string; storeUrl: string }

interface FitmentProduct {
  id: string;
  title: string;
  handle: string;
  bodyHtml: string;
  vendor: string;
  productType: string;
  tags: string[];
  publishedStatus: 'ACTIVE' | 'DRAFT' | 'ARCHIVED';
  sku:   string;
  make:  string;
  model: string;
  year:  string;
}

interface FitmentEdit {
  make:  string;
  model: string;
  year:  string;
  aiGenerated: boolean;
  confidence?: 'high' | 'medium' | 'low';
  source?: 'ori' | 'ai' | 'manual';
}

type StatusFilter = 'all' | 'uncatalogued' | 'catalogued' | 'supplier-data';
type ConfidenceFilter = 'all' | 'high' | 'medium' | 'low';

// ── Helpers ───────────────────────────────────────────────────────────────────

function isCatalogued(p: FitmentProduct, edits: Map<string, FitmentEdit>) {
  const e = edits.get(p.id);
  if (e?.aiGenerated) return false;
  // Parse raw Shopify values before checking — [""] is truthy but should NOT count as catalogued.
  // Year is optional — many accessories (chains, oils, universal parts) have no year range.
  // A product is catalogued when make + model are known; year is supplementary.
  const make  = parseTagArray(p.make).join(', ');
  const model = parseTagArray(p.model).join(', ');
  return !!make && !!model;
}

function getStatus(p: FitmentProduct, edits: Map<string, FitmentEdit>): 'catalogued' | 'ai' | 'ori' | 'manual' | 'pending' {
  if (isCatalogued(p, edits)) return 'catalogued';
  const e = edits.get(p.id);
  if (e) {
    if (e.source === 'ori') return 'ori';
    return e.aiGenerated ? 'ai' : 'manual';
  }
  return 'pending';
}

// ── Traffic-light confidence dot ────────────────────────────────────────────

const CONFIDENCE_CFG = {
  high:   { dot: 'bg-emerald-400', ring: 'ring-emerald-400/30', label: 'High confidence',   text: 'text-emerald-400' },
  medium: { dot: 'bg-amber-400',   ring: 'ring-amber-400/30',   label: 'Medium confidence', text: 'text-amber-400'   },
  low:    { dot: 'bg-red-400',     ring: 'ring-red-400/30',     label: 'Low confidence',    text: 'text-red-400'     },
} as const;

function ConfidenceDot({ level }: { level?: 'high' | 'medium' | 'low' }) {
  if (!level) return null;
  const c = CONFIDENCE_CFG[level];
  return (
    <span
      title={c.label}
      className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${c.dot} ring-2 ${c.ring}`}
    />
  );
}

function ConfidencePill({ level }: { level?: 'high' | 'medium' | 'low' }) {
  if (!level) return null;
  const c = CONFIDENCE_CFG[level];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-muted/50 border border-border/60 ${c.text} whitespace-nowrap`}>
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {level.charAt(0).toUpperCase() + level.slice(1)}
    </span>
  );
}

function StatusBadge({ status }: { status: 'catalogued' | 'ai' | 'ori' | 'manual' | 'pending' }) {
  if (status === 'catalogued') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 whitespace-nowrap">
      <CheckCircle2 className="w-3 h-3" /> Catalogued
    </span>
  );
  if (status === 'ori') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/10 border border-green-500/20 text-green-400 whitespace-nowrap">
      <FileCode className="w-3 h-3" /> Supplier Data
    </span>
  );
  if (status === 'ai') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 border border-primary/20 text-primary whitespace-nowrap">
      <Wand2 className="w-3 h-3" /> AI Generated
    </span>
  );
  if (status === 'manual') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/10 border border-amber-500/20 text-amber-400 whitespace-nowrap">
      <Pencil className="w-3 h-3" /> Edited
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-muted/50 border border-border text-muted-foreground whitespace-nowrap">
      Pending
    </span>
  );
}

// ── Tag helpers ───────────────────────────────────────────────────────────────

function parseTagArray(raw: string): string[] {
  if (!raw || !raw.trim()) return [];
  try {
    const parsed = JSON.parse(raw);
    const items = Array.isArray(parsed) ? parsed.map(String) : [String(parsed)];
    // flatMap splits any comma-joined items (e.g. old bad data ["Ford Ranger PX1,PX2,PX3"])
    return items.flatMap(item => item.split(",").map(s => s.trim())).filter(Boolean);
  } catch {
    return raw.split(",").map(s => s.trim()).filter(Boolean);
  }
}

// A1 / AI flows store models as "Make Model" pairs (e.g. "Honda CRF125F").
// Given a model string and the product's list of makes, return the make that
// owns this model by checking which make is a prefix of the model string.
// Falls back to makes[0] for unprefixed / single-make products.
function resolveModelMake(model: string, makes: string[]): string {
  if (makes.length === 0) return '(No Make)';
  if (makes.length === 1) return makes[0]!;
  if (!model) return makes[0]!;
  const modelLower = model.toLowerCase();
  const match = makes.find(m => {
    const mLower = m.toLowerCase();
    return modelLower.startsWith(mLower + ' ') || modelLower === mLower;
  });
  return match ?? makes[0]!;
}

function TagInput({ value, onChange, placeholder }: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  const tags = parseTagArray(value);
  const [input, setInput] = React.useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const commit = (raw: string) => {
    const v = raw.trim();
    if (!v) { setInput(''); return; }
    const next = tags.includes(v) ? tags : [...tags, v];
    onChange(JSON.stringify(next));
    setInput('');
  };

  const remove = (i: number) => {
    const next = tags.filter((_, idx) => idx !== i);
    onChange(next.length ? JSON.stringify(next) : '');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commit(input); }
    else if (e.key === 'Backspace' && !input && tags.length > 0) remove(tags.length - 1);
  };

  return (
    <div
      className="min-h-[34px] w-full bg-muted/50 border border-border rounded-lg px-2 py-1 flex flex-wrap gap-1 items-center cursor-text focus-within:border-primary/50 transition-colors"
      onClick={() => inputRef.current?.focus()}
    >
      {tags.map((tag, i) => (
        <span key={i} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 bg-primary/15 border border-primary/25 rounded text-xs text-primary shrink-0">
          {tag}
          <button type="button" onClick={e => { e.stopPropagation(); remove(i); }} className="hover:text-foreground transition-colors">
            <X className="w-2.5 h-2.5" />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        className="flex-1 min-w-[50px] bg-transparent text-xs text-foreground placeholder:text-slate-600 outline-none"
        placeholder={tags.length === 0 ? placeholder : 'Add…'}
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => { if (input.trim()) commit(input); }}
      />
    </div>
  );
}

// ── FitmentTagPicker ─────────────────────────────────────────────────────────
// Popover multi-select with checkboxes drawn from the loaded catalogue.
// value / onChange use the same JSON-array string format as TagInput.
// options = known values to offer as checkboxes (filtered externally for model).

function FitmentTagPicker({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder?: string;
}) {
  const selected = parseTagArray(value);
  const [open, setOpen] = React.useState(false);
  const [dropUp, setDropUp] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [addingNew, setAddingNew] = React.useState(false);
  const [newVal, setNewVal] = React.useState('');
  // rename state: which option is being renamed, and its current draft value
  const [renamingOpt, setRenamingOpt] = React.useState<string | null>(null);
  const [renameVal, setRenameVal] = React.useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const newValRef = useRef<HTMLInputElement>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);

  const DROPDOWN_HEIGHT = 300; // px — matches maxHeight below

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setDropUp(spaceBelow < DROPDOWN_HEIGHT);
    }
    setOpen(o => !o);
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
        setAddingNew(false);
        setNewVal('');
        setRenamingOpt(null);
        setRenameVal('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => { if (open) setTimeout(() => searchRef.current?.focus(), 10); }, [open]);
  useEffect(() => { if (addingNew) setTimeout(() => newValRef.current?.focus(), 10); }, [addingNew]);
  useEffect(() => { if (renamingOpt !== null) setTimeout(() => renameInputRef.current?.focus(), 10); }, [renamingOpt]);

  const toggle = (item: string) => {
    const next = selected.includes(item)
      ? selected.filter(s => s !== item)
      : [...selected, item];
    onChange(next.length ? JSON.stringify(next) : '');
  };

  const removeTag = (item: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const next = selected.filter(s => s !== item);
    onChange(next.length ? JSON.stringify(next) : '');
  };

  const commitNew = () => {
    const v = newVal.trim();
    if (!v) { setAddingNew(false); return; }
    const next = selected.includes(v) ? selected : [...selected, v];
    onChange(JSON.stringify(next));
    setNewVal('');
    setAddingNew(false);
  };

  const startRename = (opt: string) => {
    setRenamingOpt(opt);
    setRenameVal(opt);
    setAddingNew(false);
  };

  const commitRename = () => {
    const newName = renameVal.trim();
    if (newName && newName !== renamingOpt) {
      // If the old value was selected, replace it with the new name
      if (selected.includes(renamingOpt!)) {
        const next = selected.map(s => s === renamingOpt ? newName : s);
        onChange(JSON.stringify(next));
      }
    }
    setRenamingOpt(null);
    setRenameVal('');
  };

  const cancelRename = () => { setRenamingOpt(null); setRenameVal(''); };

  const filtered = options.filter(o => !search || o.toLowerCase().includes(search.toLowerCase()));

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger */}
      <div
        className="min-h-[34px] w-full bg-muted/50 border border-border rounded-lg px-2 py-1 flex flex-wrap gap-1 items-center cursor-pointer hover:border-primary/40 transition-colors"
        onClick={handleOpen}
      >
        {selected.map(tag => (
          <span key={tag} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 bg-primary/15 border border-primary/25 rounded text-xs text-primary shrink-0">
            {tag}
            <button type="button" onClick={e => removeTag(tag, e)} className="hover:text-foreground transition-colors">
              <X className="w-2.5 h-2.5" />
            </button>
          </span>
        ))}
        {selected.length === 0 && (
          <span className="text-xs text-slate-600 flex-1">{placeholder ?? 'Select…'}</span>
        )}
        <ChevronDown className={`w-3 h-3 text-muted-foreground ml-auto shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </div>

      {/* Dropdown */}
      {open && (
        <div
          className={`absolute z-50 w-full min-w-[200px] bg-popover border border-border rounded-lg shadow-lg overflow-hidden flex flex-col ${dropUp ? 'bottom-full mb-1' : 'mt-1'}`}
          style={{ maxHeight: '300px' }}
          onClick={e => e.stopPropagation()}
        >
          {/* Search */}
          <div className="p-2 border-b border-border flex-shrink-0">
            <input
              ref={searchRef}
              className="w-full bg-muted/50 rounded px-2 py-1 text-xs outline-none border border-border/50 focus:border-primary/40 transition-colors placeholder:text-slate-600"
              placeholder="Search…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          {/* Options */}
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 && (
              <p className="text-xs text-muted-foreground p-3 text-center">No options — use + to add</p>
            )}
            {filtered.map(opt => (
              renamingOpt === opt ? (
                /* ── Inline rename row ── */
                <div key={opt} className="flex items-center gap-1 px-2 py-1.5 bg-muted/60">
                  <input
                    ref={renameInputRef}
                    className="flex-1 min-w-0 bg-background rounded px-2 py-0.5 text-xs outline-none border border-primary/40 transition-colors"
                    value={renameVal}
                    onChange={e => setRenameVal(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
                      if (e.key === 'Escape') cancelRename();
                    }}
                  />
                  <button type="button" onClick={commitRename} title="Save" className="p-1 rounded text-emerald-500 hover:bg-emerald-500/10 transition-colors">
                    <Check className="w-3 h-3" />
                  </button>
                  <button type="button" onClick={cancelRename} title="Cancel" className="p-1 rounded text-muted-foreground hover:bg-muted transition-colors">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                /* ── Normal option row ── */
                <div key={opt} className="group flex items-center gap-2 px-3 py-1.5 hover:bg-muted/50">
                  <label className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selected.includes(opt)}
                      onChange={() => toggle(opt)}
                      className="accent-primary shrink-0"
                    />
                    <span className="text-xs text-foreground truncate">{opt}</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => startRename(opt)}
                    title="Rename"
                    className="opacity-0 group-hover:opacity-100 p-1 rounded text-muted-foreground hover:text-foreground transition-all shrink-0"
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                </div>
              )
            ))}
          </div>
          {/* Add new */}
          <div className="border-t border-border p-2 flex-shrink-0">
            {addingNew ? (
              <div className="flex gap-1">
                <input
                  ref={newValRef}
                  className="flex-1 min-w-0 bg-muted/50 rounded px-2 py-1 text-xs outline-none border border-border/50 focus:border-primary/40 transition-colors"
                  placeholder="New value…"
                  value={newVal}
                  onChange={e => setNewVal(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { e.preventDefault(); commitNew(); }
                    if (e.key === 'Escape') { setAddingNew(false); setNewVal(''); }
                  }}
                />
                <button type="button" onClick={commitNew} className="px-2 py-1 bg-primary/20 rounded text-xs text-primary hover:bg-primary/30 transition-colors whitespace-nowrap">Add</button>
                <button type="button" onClick={() => { setAddingNew(false); setNewVal(''); }} className="px-2 py-1 rounded text-xs text-muted-foreground hover:bg-muted transition-colors">✕</button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => { setAddingNew(true); setRenamingOpt(null); }}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <Plus className="w-3 h-3" /> Add new value
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Widget Config ─────────────────────────────────────────────────────────────

const VFF_BUTTON_ICONS: { id: string; label: string; svg: string }[] = [
  { id: 'car',         label: 'Car',    svg: '<path d="M80 224l37.78-88.15C123.93 121.5 139.6 112 157.11 112h197.78c17.51 0 33.18 9.5 39.33 23.85L432 224M80 224h352v144H80zM112 368v32H80v-32M432 368v32h-32v-32"/><circle cx="144" cy="288" r="16"/><circle cx="368" cy="288" r="16"/>' },
  { id: 'search',      label: 'Search', svg: '<circle cx="220" cy="220" r="140"/><path d="M338 338L452 452"/>' },
  { id: 'arrow-right', label: 'Arrow',  svg: '<path d="M80 256h352M304 128l144 128-144 128"/>' },
  { id: 'check',       label: 'Tick',   svg: '<path d="M64 256l144 160 240-272"/>' },
  { id: 'filter',      label: 'Filter', svg: '<path d="M48 112L464 112L304 288v112l-96 32V288z"/>' },
  { id: 'bolt',        label: 'Bolt',   svg: '<path d="M304 48H192L96 272h128L112 464l304-240H272z"/>' },
  { id: 'truck',       label: 'Truck',  svg: '<rect x="32" y="176" width="288" height="160" rx="16"/><path d="M320 208h80l48 96v32H320V208z"/><circle cx="112" cy="368" r="36"/><circle cx="368" cy="368" r="36"/>' },
  { id: 'star',        label: 'Star',   svg: '<polygon points="256,64 308,208 464,208 340,298 388,448 256,360 124,448 172,298 48,208 204,208"/>' },
];

interface WidgetConfig {
  showMake: boolean;
  showModel: boolean;
  showYear: boolean;
  showProductType: boolean;
  productTypeFilter: string[];
  buttonText: string;
  buttonColor: string;
  defaultProductType: string;
  hideProductTypeDropdown: boolean;
  loadingText: string;
  resetText: string;
  buttonIcon: string;
  badgeAllowedProductTypes: string;
  badgeNoVehicleText: string;
  badgeNoVehicleLink: string;
  badgeIcon: string;
  showOnHome: boolean;
  showOnCollection: boolean;
  showOnProduct: boolean;
}

const DEFAULT_WIDGET_CONFIG: WidgetConfig = {
  showMake: true,
  showModel: true,
  showYear: true,
  showProductType: false,
  productTypeFilter: [],
  buttonText: '',
  buttonColor: '',
  defaultProductType: '',
  hideProductTypeDropdown: false,
  loadingText: '',
  resetText: '',
  badgeAllowedProductTypes: '',
  badgeNoVehicleText: '',
  badgeNoVehicleLink: '',
  badgeIcon: 'car',
  buttonIcon: 'car',
  showOnHome: true,
  showOnCollection: true,
  showOnProduct: true,
  resetBgColor: '',
  resetTextColor: '',
};

// ── Widget Preview srcdoc builder ─────────────────────────────────────────────
// Constructs a fully self-contained HTML page that renders the VFF widget with
// mock data instead of live API calls.  The fetch() interceptor below is
// injected BEFORE vehicle-filter.js so the widget boots normally but resolves
// its options request from static data.

function buildPreviewSrcdoc(
  baseCss: string,
  customCss: string,
  baseJs: string,
  config: WidgetConfig = DEFAULT_WIDGET_CONFIG,
): string {
  // productTypeFilter is a DENY list (hidden types). Empty = show all.
  const DEFAULT_MOCK_TYPES = ['Accessories', 'Bull Bars', 'Lift Kits', 'Roof Racks', 'Side Steps', 'Tyres', 'Wheels'];
  const mockProductTypes = config.productTypeFilter.length > 0
    ? DEFAULT_MOCK_TYPES.filter(t => !config.productTypeFilter.includes(t))
    : DEFAULT_MOCK_TYPES;

  const mockIntercept = `(function(){
var MOCK={
  makes:["Chevrolet","Dodge","Ford","Holden","Honda","Jeep","Mazda","Mitsubishi","Nissan","Subaru","Toyota"],
  models:{
    "Chevrolet":["Blazer","Colorado","Silverado 1500","Silverado 2500HD","Tahoe","Traverse"],
    "Dodge":["Challenger","Charger","Durango","Ram 1500"],
    "Ford":["Bronco","Edge","Escape","Explorer","F-150","F-250","Mustang","Ranger"],
    "Holden":["Colorado","Commodore","Rodeo","Trailblazer"],
    "Honda":["CR-V","HR-V","Pilot","Ridgeline"],
    "Jeep":["Cherokee","Gladiator","Grand Cherokee","Wrangler"],
    "Mazda":["BT-50","CX-5","CX-9"],
    "Mitsubishi":["Eclipse Cross","Outlander","Pajero","Triton"],
    "Nissan":["Frontier","Navara","Pathfinder","Patrol","Titan"],
    "Subaru":["Forester","Outback","WRX"],
    "Toyota":["4Runner","Camry","HiLux","Highlander","Land Cruiser","Prado","RAV4","Sequoia","Tacoma","Tundra"]
  },
  years:{}
};
Object.keys(MOCK.models).forEach(function(make){
  MOCK.models[make].forEach(function(model){
    var ys=[];for(var y=2024;y>=2015;y--)ys.push(String(y));
    MOCK.years[make+"|||"+model]=ys;
  });
});
var _f=window.fetch;
window.fetch=function(url){
  if(typeof url==="string"&&url.indexOf("/api/public/fitment/options")!==-1){
    return Promise.resolve({ok:true,json:function(){return Promise.resolve(MOCK);}});
  }
  return _f.apply(this,arguments);
};
/* Prevent form navigation inside the preview iframe */
document.addEventListener("submit",function(e){e.preventDefault();},true);
})();`;

  const makeField = config.showMake ? `
      <div class="vff-field">
        <label class="vff-label">Make</label>
        <select class="vff-select" data-role="make">
          <option value="">Select Make</option>
        </select>
      </div>` : '';

  const modelField = config.showModel ? `
      <div class="vff-field">
        <label class="vff-label">Model</label>
        <select class="vff-select" data-role="model">
          <option value="">Select Model</option>
        </select>
      </div>` : '';

  const yearField = config.showYear ? `
      <div class="vff-field">
        <label class="vff-label">Year</label>
        <select class="vff-select" data-role="year">
          <option value="">Select Year</option>
        </select>
      </div>` : '';

  // Product type field: render only a labelled anchor div — NO .vff-nice-select
  // pre-built here. vehicle-filter.js init() purges ALL .vff-nice-select elements
  // at startup (cleanup from previous init), so anything pre-built gets deleted.
  // Instead we inject a post-init script (after ${baseJs}) that builds the
  // dropdown dynamically once the widget has already finished its cleanup pass.
  const productTypeField = config.showProductType ? `
      <div class="vff-field" data-vff-pt-field>
        <label class="vff-label">Product Type</label>
      </div>` : '';

  const productTypeScript = config.showProductType ? `
<script>
(function () {
  var types = ${JSON.stringify(mockProductTypes)};
  var field = document.querySelector('[data-vff-pt-field]');
  if (!field) return;

  var wrapper = document.createElement('div');
  wrapper.className = 'vff-nice-select';
  wrapper.setAttribute('tabindex', '0');
  wrapper.setAttribute('role', 'combobox');
  wrapper.setAttribute('aria-expanded', 'false');
  wrapper.setAttribute('aria-haspopup', 'listbox');

  var current = document.createElement('span');
  current.className = 'vff-current';
  current.textContent = 'All Types';

  var caret = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  caret.setAttribute('class', 'vff-caret');
  caret.setAttribute('viewBox', '0 0 512 512');
  caret.setAttribute('width', '14');
  caret.setAttribute('height', '14');
  caret.setAttribute('aria-hidden', 'true');
  var caretPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  caretPath.setAttribute('d', 'M112 184l144 144 144-144');
  caretPath.setAttribute('fill', 'none');
  caretPath.setAttribute('stroke', 'currentColor');
  caretPath.setAttribute('stroke-width', '48');
  caretPath.setAttribute('stroke-linecap', 'round');
  caretPath.setAttribute('stroke-linejoin', 'round');
  caret.appendChild(caretPath);

  var dropdown = document.createElement('div');
  dropdown.className = 'vff-dropdown';
  dropdown.setAttribute('role', 'listbox');
  var list = document.createElement('ul');
  list.className = 'vff-list';
  list.setAttribute('role', 'listbox');

  [''].concat(types).forEach(function (t) {
    var li = document.createElement('li');
    li.className = 'vff-option' + (t === '' ? ' vff-selected' : '');
    li.setAttribute('data-value', t);
    li.setAttribute('role', 'option');
    li.setAttribute('aria-selected', t === '' ? 'true' : 'false');
    li.textContent = t || 'All Types';
    list.appendChild(li);
  });
  dropdown.appendChild(list);

  wrapper.appendChild(current);
  wrapper.appendChild(caret);
  wrapper.appendChild(dropdown);
  field.appendChild(wrapper);

  wrapper.addEventListener('click', function (e) {
    e.stopPropagation();
    var open = wrapper.classList.toggle('vff-open');
    wrapper.setAttribute('aria-expanded', open ? 'true' : 'false');
  });
  document.addEventListener('click', function () {
    wrapper.classList.remove('vff-open');
    wrapper.setAttribute('aria-expanded', 'false');
  });
  list.addEventListener('click', function (e) {
    var li = e.target.closest('li.vff-option');
    if (!li) return;
    list.querySelectorAll('.vff-selected').forEach(function (el) {
      el.classList.remove('vff-selected');
      el.setAttribute('aria-selected', 'false');
    });
    li.classList.add('vff-selected');
    li.setAttribute('aria-selected', 'true');
    current.textContent = li.textContent;
    current.classList.toggle('vff-has-value', li.getAttribute('data-value') !== '');
  });
})();
</script>` : '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
body{background:#111;min-height:100vh}
${baseCss}
${customCss ? `/* ── Custom CSS ──────────────────────────────────────────────── */\n${customCss}` : ''}
${config.buttonColor ? `.vff-submit:not([data-vff-mode="reset"]) { background: ${config.buttonColor} !important; }` : ''}
${config.resetBgColor ? `.vff-submit[data-vff-mode="reset"], .vff-submit[data-vff-mode="reset"]:hover:not(:disabled) { background: ${config.resetBgColor} !important; border-color: ${config.resetBgColor} !important; }` : ''}
${config.resetTextColor ? `.vff-submit[data-vff-mode="reset"] { color: ${config.resetTextColor} !important; }` : ''}
</style>
</head>
<body>
<div class="vff-root"
  data-api-base="https://demo.soraflo.app"
  data-shop="preview.myshopify.com"
  data-namespace="custom"
  data-make-key="make"
  data-model-key="model"
  data-year-key="year"
  data-collection-url="/collections/all"
>
  <h2 class="vff-heading">Find Parts For Your Vehicle</h2>
  <p class="vff-subheading">Select your vehicle below to find compatible parts and accessories.</p>
  <form class="vff-form">
    <div class="vff-selects">${makeField}${modelField}${yearField}${productTypeField}
      <div class="vff-field vff-btn-field">
        <button type="submit" class="vff-submit" disabled>
          <svg class="vff-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          <span class="vff-btn-text">${config.buttonText || 'Find Parts'}</span>
        </button>
      </div>
    </div>
    <p class="vff-status"></p>
  </form>
</div>
<script>${mockIntercept}</script>
<script>${baseJs}</script>
${productTypeScript}
</body>
</html>`;
}

// ── A1 Fitment Types ──────────────────────────────────────────────────────────

interface A1Product {
  id: string;
  title: string;
  handle: string;
  status: string;
  sku: string;
  make: string;
  model: string;
  year: string;
}

interface A1Result {
  productId: string;
  title: string;
  sku: string;
  status: 'found' | 'not-found' | 'no-sku';
  make?: string;
  model?: string;
  year?: string;
}

// ── A1 Fitment Section ────────────────────────────────────────────────────────

function A1FitmentSection({ stores, token }: { stores: ShopifyStore[]; token: string }) {
  const { toast } = useToast();
  const [a1StoreId,    setA1StoreId]    = useState('');
  const [a1Products,   setA1Products]   = useState<A1Product[]>([]);
  const [a1Loading,    setA1Loading]    = useState(false);
  const [a1Loaded,     setA1Loaded]     = useState(false);
  const [a1Selected,   setA1Selected]   = useState<Set<string>>(new Set());
  const [a1Generating, setA1Generating] = useState(false);
  const [a1Progress,   setA1Progress]   = useState({ current: 0, total: 0 });
  const [a1Results,    setA1Results]    = useState<Map<string, A1Result>>(new Map());
  const [a1Applying,   setA1Applying]   = useState(false);
  const [a1ApplyResult, setA1ApplyResult] = useState<{
    ok: boolean;
    message: string;
    failed?: Array<{ productId: string; title: string; sku: string; reason: string }>;
  } | null>(null);
  const [a1FailedExpanded, setA1FailedExpanded] = useState(false);
  const [a1Search,     setA1Search]     = useState('');
  const [a1Filter,     setA1Filter]     = useState<'all' | 'found' | 'not-found' | 'no-sku' | 'pending'>('all');

  // Makes allow/disallow config
  const [a1Makes,        setA1Makes]        = useState<{ id: number; makeName: string; allowed: boolean; discovered: boolean }[]>([]);
  const [a1MakesLoading, setA1MakesLoading] = useState(false);

  useEffect(() => {
    if (!a1StoreId && stores.length === 1) setA1StoreId(stores[0]!.id);
  }, [stores, a1StoreId]);

  const loadA1Makes = useCallback(async (storeId: string) => {
    setA1MakesLoading(true);
    try {
      const r = await fetch(`${BASE}/api/a1-fitment/makes?storeId=${storeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error ?? 'Failed to load makes');
      setA1Makes(d.makes ?? []);
    } catch (err: any) {
      toast({ title: err.message ?? 'Failed to load makes', variant: 'destructive' });
    } finally {
      setA1MakesLoading(false);
    }
  }, [token, toast]);

  useEffect(() => {
    if (a1StoreId) loadA1Makes(a1StoreId);
    else setA1Makes([]);
  }, [a1StoreId, loadA1Makes]);

  const toggleA1Make = useCallback(async (makeId: number, currentAllowed: boolean) => {
    const newAllowed = !currentAllowed;
    // Optimistic update
    setA1Makes(prev => prev.map(m => m.id === makeId ? { ...m, allowed: newAllowed } : m));
    try {
      const r = await fetch(`${BASE}/api/a1-fitment/makes`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeId: a1StoreId, makeId, allowed: newAllowed }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error ?? 'Toggle failed');
    } catch (err: any) {
      // Revert on error
      setA1Makes(prev => prev.map(m => m.id === makeId ? { ...m, allowed: currentAllowed } : m));
      toast({ title: err.message ?? 'Failed to toggle make', variant: 'destructive' });
    }
  }, [a1StoreId, token, toast]);

  const loadA1Products = useCallback(async () => {
    if (!a1StoreId) { toast({ title: 'Select a store first', variant: 'destructive' }); return; }
    setA1Loading(true);
    setA1Loaded(false);
    setA1Products([]);
    setA1Selected(new Set());
    setA1Results(new Map());
    setA1ApplyResult(null);
    try {
      const r = await fetch(`${BASE}/api/a1-fitment/products?storeId=${a1StoreId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error ?? 'Failed to load products');
      setA1Products(d.products ?? []);
      setA1Loaded(true);
      toast({ title: `${(d.products ?? []).length} products loaded` });
    } catch (err: any) {
      toast({ title: err.message ?? 'Failed to load', variant: 'destructive' });
    } finally {
      setA1Loading(false);
    }
  }, [a1StoreId, token, toast]);

  const runA1Lookup = useCallback(async () => {
    const toProcess = a1Products.filter(p => a1Selected.has(p.id));
    if (toProcess.length === 0) { toast({ title: 'Select products first', variant: 'destructive' }); return; }
    setA1Generating(true);
    setA1Progress({ current: 0, total: toProcess.length });
    setA1ApplyResult(null);
    try {
      const resp = await fetch(`${BASE}/api/a1-fitment/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          storeId: a1StoreId,
          products: toProcess.map(p => ({ id: p.id, title: p.title, sku: p.sku })),
        }),
      });
      if (!resp.ok || !resp.body) throw new Error('Stream failed to start');
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop() ?? '';
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith('data:')) continue;
          try {
            const ev = JSON.parse(line.slice(5).trim());
            if (ev.type === 'progress') {
              setA1Progress({ current: ev.current, total: ev.total });
            } else if (ev.type === 'result') {
              setA1Results(prev => { const next = new Map(prev); next.set(ev.productId, ev as A1Result); return next; });
            } else if (ev.type === 'error') {
              toast({ title: ev.message ?? 'Lookup failed', variant: 'destructive' });
            }
          } catch { /* ignore */ }
        }
      }
    } catch (err: any) {
      toast({ title: err.message ?? 'Lookup failed', variant: 'destructive' });
    } finally {
      setA1Generating(false);
    }
  }, [a1Products, a1Selected, a1StoreId, token, toast]);

  const applyA1Fitment = useCallback(async () => {
    const updates = [...a1Results.entries()]
      .filter(([, r]) => r.status === 'found' && r.make && r.model && r.year)
      .map(([productId, r]) => ({ productId, make: r.make!, model: r.model!, year: r.year! }));
    if (updates.length === 0) { toast({ title: 'No results to apply', variant: 'destructive' }); return; }
    setA1Applying(true);
    setA1ApplyResult(null);
    setA1FailedExpanded(false);
    try {
      const r = await fetch(`${BASE}/api/fitment/apply`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeId: a1StoreId, updates }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error ?? 'Apply failed');
      const failed = (d.errors ?? []).map((e: { productId: string; message: string }) => {
        const prod = a1Products.find(p => p.id === e.productId);
        return { productId: e.productId, title: prod?.title ?? e.productId, sku: prod?.sku ?? '', reason: e.message };
      });
      const msg = d.errorCount > 0
        ? `${d.successCount} updated, ${d.errorCount} failed`
        : `${d.successCount} product${d.successCount !== 1 ? 's' : ''} updated in Shopify`;
      setA1ApplyResult({ ok: d.errorCount === 0, message: msg, failed });
      toast({ title: `✓ ${d.successCount} product fitments saved to Shopify` });
    } catch (err: any) {
      setA1ApplyResult({ ok: false, message: err.message ?? 'Apply failed' });
      toast({ title: err.message ?? 'Apply failed', variant: 'destructive' });
    } finally {
      setA1Applying(false);
    }
  }, [a1Results, a1Products, a1StoreId, token, toast]);

  const retryFailedA1Fitment = useCallback(async () => {
    const failedIds = new Set((a1ApplyResult?.failed ?? []).map(f => f.productId));
    if (failedIds.size === 0) return;
    const updates = [...a1Results.entries()]
      .filter(([productId, r]) => failedIds.has(productId) && r.status === 'found' && r.make && r.model && r.year)
      .map(([productId, r]) => ({ productId, make: r.make!, model: r.model!, year: r.year! }));
    if (updates.length === 0) {
      toast({ title: 'No failed products have valid fitment data to retry', variant: 'destructive' });
      return;
    }
    setA1Applying(true);
    setA1ApplyResult(null);
    setA1FailedExpanded(false);
    try {
      const r = await fetch(`${BASE}/api/fitment/apply`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeId: a1StoreId, updates }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error ?? 'Retry failed');
      const failed = (d.errors ?? []).map((e: { productId: string; message: string }) => {
        const prod = a1Products.find(p => p.id === e.productId);
        return { productId: e.productId, title: prod?.title ?? e.productId, sku: prod?.sku ?? '', reason: e.message };
      });
      const msg = d.errorCount > 0
        ? `${d.successCount} fixed, ${d.errorCount} still failing`
        : `${d.successCount} product${d.successCount !== 1 ? 's' : ''} fixed`;
      setA1ApplyResult({ ok: d.errorCount === 0, message: msg, failed });
      toast({ title: `✓ ${d.successCount} retry${d.successCount !== 1 ? 's' : ''} saved to Shopify` });
    } catch (err: any) {
      setA1ApplyResult({ ok: false, message: err.message ?? 'Retry failed' });
      toast({ title: err.message ?? 'Retry failed', variant: 'destructive' });
    } finally {
      setA1Applying(false);
    }
  }, [a1ApplyResult, a1Results, a1Products, a1StoreId, token, toast]);

  const filteredA1Products = useMemo(() => {
    let list = a1Products;
    if (a1Search.trim()) {
      const q = a1Search.toLowerCase();
      list = list.filter(p => p.title.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q));
    }
    if (a1Filter !== 'all') {
      list = list.filter(p => {
        const r = a1Results.get(p.id);
        if (a1Filter === 'pending') return !r;
        return r?.status === a1Filter;
      });
    }
    return list;
  }, [a1Products, a1Search, a1Filter, a1Results]);

  const foundCount    = useMemo(() => [...a1Results.values()].filter(r => r.status === 'found').length,     [a1Results]);
  const notFoundCount = useMemo(() => [...a1Results.values()].filter(r => r.status === 'not-found').length, [a1Results]);
  const noSkuCount    = useMemo(() => [...a1Results.values()].filter(r => r.status === 'no-sku').length,    [a1Results]);

  const allA1Visible = filteredA1Products.length > 0 && filteredA1Products.every(p => a1Selected.has(p.id));

  const toggleA1All = () => {
    if (allA1Visible) {
      setA1Selected(prev => { const n = new Set(prev); filteredA1Products.forEach(p => n.delete(p.id)); return n; });
    } else {
      setA1Selected(prev => { const n = new Set(prev); filteredA1Products.forEach(p => n.add(p.id)); return n; });
    }
  };

  const toggleA1 = (id: string) => {
    setA1Selected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  return (
    <div className="space-y-5">
      {/* Store picker + Load */}
      <div className="bg-muted/30 border border-border/60 rounded-2xl p-5">
        <div className="flex flex-wrap items-end gap-3">
          <ShopifyTargetStoreSelector
            stores={stores}
            value={a1StoreId}
            onChange={setA1StoreId}
            label="Target Shopify store"
            className="flex-1 min-w-[180px]"
          />
          <button
            onClick={loadA1Products}
            disabled={a1Loading || !a1StoreId}
            className="flex items-center gap-2 px-5 py-2 rounded-xl bg-primary/20 hover:bg-primary/30 border border-primary/30 text-primary text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {a1Loading
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Loading…</>
              : <><RefreshCw className="w-4 h-4" /> {a1Loaded ? 'Reload' : 'Load Products'}</>}
          </button>
        </div>
      </div>

      {/* Makes allow / disallow config */}
      {a1StoreId && (
        <div className="bg-muted/30 border border-border/60 rounded-2xl p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings2 className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-foreground">Make Filter</span>
              <span className="text-xs text-muted-foreground">— click any make to toggle it</span>
            </div>
            {a1MakesLoading && <Loader2 className="w-3.5 h-3.5 text-muted-foreground animate-spin" />}
          </div>

          {a1Makes.length === 0 && !a1MakesLoading && (
            <p className="text-xs text-muted-foreground">No makes configured yet — they will be seeded when you load products.</p>
          )}

          {a1Makes.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Allowed */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-emerald-400 uppercase tracking-wide">
                  Allowed ({a1Makes.filter(m => m.allowed).length})
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {a1Makes.filter(m => m.allowed).map(m => (
                    <button
                      key={m.id}
                      onClick={() => toggleA1Make(m.id, m.allowed)}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-500/15 hover:bg-red-500/15 border border-emerald-500/25 hover:border-red-500/25 text-emerald-700 dark:text-emerald-300 hover:text-red-700 dark:hover:text-red-300 text-xs font-medium transition-all group"
                      title="Click to disallow"
                    >
                      {m.makeName}
                      {m.discovered && <span className="opacity-50 text-[10px]">*</span>}
                      <X className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  ))}
                </div>
              </div>

              {/* Disallowed */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Disallowed ({a1Makes.filter(m => !m.allowed).length})
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {a1Makes.filter(m => !m.allowed).map(m => (
                    <button
                      key={m.id}
                      onClick={() => toggleA1Make(m.id, m.allowed)}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-muted/50 hover:bg-emerald-500/15 border border-border hover:border-emerald-500/25 text-muted-foreground hover:text-emerald-700 dark:hover:text-emerald-300 text-xs font-medium transition-all group"
                      title="Click to allow"
                    >
                      {m.makeName}
                      {m.discovered && <span className="opacity-50 text-[10px]">*</span>}
                      <Check className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {a1Makes.some(m => m.discovered) && (
            <p className="text-[11px] text-slate-600">* discovered automatically from A1 catalogue data</p>
          )}
        </div>
      )}

      {/* Info banner */}
      {!a1Loaded && !a1Loading && (
        <div className="flex items-start gap-3 px-4 py-3 bg-primary/5 border border-primary/15 rounded-xl text-sm text-muted-foreground">
          <Car className="w-4 h-4 text-primary mt-0.5 shrink-0" />
          <div>
            <span className="text-foreground font-medium">A1 Accessory Fitment Lookup.</span>{' '}
            Loads all products from your store, looks up fitment data from the A1 Accessory Imports catalogue by SKU, and writes Make / Model / Year metafields directly to Shopify.
          </div>
        </div>
      )}

      {/* Stats */}
      {a1Results.size > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Processed',  value: a1Results.size,  color: 'text-foreground'       },
            { label: 'Found',      value: foundCount,      color: 'text-emerald-400' },
            { label: 'Not Found',  value: notFoundCount,   color: 'text-amber-400'   },
            { label: 'No SKU',     value: noSkuCount,      color: 'text-muted-foreground'   },
          ].map(s => (
            <div key={s.label} className="bg-muted/30 border border-border/60 rounded-xl px-4 py-3">
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Apply result banner */}
      {a1ApplyResult && (
        <div className="flex flex-col gap-2">
          <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-sm ${
            a1ApplyResult.ok
              ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
              : 'bg-amber-500/10 border-amber-500/20 text-amber-400'
          }`}>
            {a1ApplyResult.ok ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertTriangle className="w-4 h-4 shrink-0" />}
            <span className="flex-1">{a1ApplyResult.message}</span>
            {(a1ApplyResult.failed?.length ?? 0) > 0 && (
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={retryFailedA1Fitment}
                  disabled={a1Applying}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/30 text-amber-300 text-xs font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {a1Applying
                    ? <><Loader2 className="w-3 h-3 animate-spin" /> Retrying…</>
                    : <><RefreshCw className="w-3 h-3" /> Retry Failed ({a1ApplyResult.failed!.length})</>}
                </button>
                <button
                  onClick={() => setA1FailedExpanded(v => !v)}
                  className="text-xs underline underline-offset-2 opacity-80 hover:opacity-100"
                >
                  {a1FailedExpanded ? 'Hide' : 'Show'}
                </button>
              </div>
            )}
          </div>
          {a1FailedExpanded && (a1ApplyResult.failed?.length ?? 0) > 0 && (
            <div className="rounded-xl border border-border overflow-hidden">
              <div className="px-4 py-2 bg-muted/50 border-b border-border flex items-center justify-between">
                <span className="text-xs font-medium text-foreground">Failed products</span>
                <span className="text-xs text-muted-foreground">{a1ApplyResult.failed!.length} product{a1ApplyResult.failed!.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="divide-y divide-white/5 max-h-64 overflow-y-auto">
                {a1ApplyResult.failed!.map(f => (
                  <div key={f.productId} className="px-4 py-2.5 flex items-start gap-3">
                    <XCircle className="w-3.5 h-3.5 text-red-400 shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-foreground truncate">{f.title}</p>
                      {f.sku && <p className="text-xs text-muted-foreground font-mono">{f.sku}</p>}
                      <p className="text-xs text-red-400/80 mt-0.5">{f.reason}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Toolbar */}
      {a1Loaded && a1Products.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1 p-1 bg-muted/50 border border-border/50 rounded-xl">
            {(['all', 'pending', 'found', 'not-found', 'no-sku'] as const).map(f => (
              <button
                key={f}
                onClick={() => setA1Filter(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  a1Filter === f
                    ? 'bg-primary/20 border border-primary/30 text-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                {f === 'not-found' ? 'Not Found' : f === 'no-sku' ? 'No SKU' : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 flex-1 min-w-[160px] max-w-xs px-3 py-2 bg-muted/50 border border-border rounded-xl focus-within:border-primary/50 transition-colors">
            <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <input
              className="flex-1 bg-transparent text-sm text-foreground placeholder:text-slate-600 outline-none"
              placeholder="Search title or SKU…"
              value={a1Search}
              onChange={e => setA1Search(e.target.value)}
            />
            {a1Search && (
              <button onClick={() => setA1Search('')} className="text-muted-foreground hover:text-foreground">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <div className="flex-1" />

          <button
            onClick={runA1Lookup}
            disabled={a1Generating || a1Selected.size === 0}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/20 hover:bg-primary/30 border border-primary/30 text-primary text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {a1Generating ? (
              <><Loader2 className="w-4 h-4 animate-spin" />
                {a1Progress.total > 0 ? `${a1Progress.current}/${a1Progress.total}` : 'Looking up…'}
              </>
            ) : (
              <><Search className="w-4 h-4" /> Lookup A1{a1Selected.size > 0 ? ` (${a1Selected.size})` : ''}</>
            )}
          </button>

          {foundCount > 0 && (
            <button
              onClick={applyA1Fitment}
              disabled={a1Applying || a1Generating}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {a1Applying
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Applying…</>
                : <><Check className="w-4 h-4" /> Apply to Shopify ({foundCount})</>}
            </button>
          )}
        </div>
      )}

      {/* Product table */}
      {a1Loaded && (
        <div className="bg-muted/30 border border-border/60 rounded-2xl overflow-hidden">
          {filteredA1Products.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/60 text-xs text-muted-foreground uppercase tracking-wider">
                  <th className="py-3 pl-4 pr-2 w-8">
                    <input
                      type="checkbox"
                      checked={allA1Visible}
                      onChange={toggleA1All}
                      className="w-3.5 h-3.5 rounded accent-primary cursor-pointer"
                    />
                  </th>
                  <th className="py-3 px-3 text-left">Product</th>
                  <th className="py-3 px-3 text-left">SKU</th>
                  <th className="py-3 px-3 text-left">Make</th>
                  <th className="py-3 px-3 text-left">Models</th>
                  <th className="py-3 px-3 text-left">Years</th>
                  <th className="py-3 px-3 text-left">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filteredA1Products.map(p => {
                  const result  = a1Results.get(p.id);
                  const sel     = a1Selected.has(p.id);
                  const yearArr = result?.year ? result.year.split(',') : [];
                  return (
                    <tr
                      key={p.id}
                      onClick={() => toggleA1(p.id)}
                      className={`cursor-pointer transition-colors ${sel ? 'bg-primary/5 hover:bg-primary/8' : 'hover:bg-muted/50'}`}
                    >
                      <td className="py-3 pl-4 pr-2">
                        <input type="checkbox" checked={sel} onChange={() => {}} className="w-3.5 h-3.5 rounded accent-primary cursor-pointer" />
                      </td>
                      <td className="py-3 px-3 max-w-[200px]">
                        <span className="text-foreground text-xs leading-snug line-clamp-2">{p.title}</span>
                      </td>
                      <td className="py-3 px-3">
                        {p.sku
                          ? <span className="font-mono text-xs text-muted-foreground">{p.sku}</span>
                          : <em className="text-slate-600 text-xs">—</em>}
                      </td>
                      <td className="py-3 px-3 text-xs text-foreground">
                        {result?.make ?? <span className="text-slate-600">—</span>}
                      </td>
                      <td className="py-3 px-3 max-w-[220px]">
                        <span className="text-xs text-foreground leading-snug line-clamp-2">
                          {result?.model
                            ? result.model.split(',').slice(0, 3).join(', ') + (result.model.split(',').length > 3 ? '…' : '')
                            : <span className="text-slate-600">—</span>}
                        </span>
                      </td>
                      <td className="py-3 px-3 whitespace-nowrap text-xs text-muted-foreground">
                        {yearArr.length > 0
                          ? `${yearArr[0]}–${yearArr[yearArr.length - 1]} (${yearArr.length})`
                          : <span className="text-slate-600">—</span>}
                      </td>
                      <td className="py-3 px-3">
                        {!result ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-muted/50 border border-border text-muted-foreground">Pending</span>
                        ) : result.status === 'found' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
                            <CheckCircle2 className="w-2.5 h-2.5" />Found
                          </span>
                        ) : result.status === 'no-sku' ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-muted/50 border border-border text-muted-foreground">No SKU</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-500/10 border border-amber-500/20 text-amber-400">
                            <XCircle className="w-2.5 h-2.5" />Not Found
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="py-16 text-center text-slate-600 text-sm">
              {a1Products.length === 0
                ? 'Load products from your store to begin'
                : 'No products match the current filter'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export function Fitment() {
  const { token } = useAuthStore();
  const { toast } = useToast();

  // ── Store ──────────────────────────────────────────────────────────────────
  const [selectedStoreId, setSelectedStoreId] = useState('');

  const { data: storesData } = useQuery<ShopifyStore[]>({
    queryKey: ['stores'],
    enabled: !!token,
    queryFn: async () => {
      const r = await fetch(`${BASE}/api/stores`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      return Array.isArray(d) ? d : (d.stores ?? []);
    },
  });
  const stores = storesData ?? [];

  // Auto-select first store
  useEffect(() => {
    if (!selectedStoreId && stores.length === 1) setSelectedStoreId(stores[0]!.id);
  }, [stores, selectedStoreId]);

  // ── Metafield config ───────────────────────────────────────────────────────
  const [showConfig, setShowConfig] = useState(false);
  const [namespace, setNamespace] = useState('custom');
  const [makeKey,   setMakeKey]   = useState('make');
  const [modelKey,  setModelKey]  = useState('model');
  const [yearKey,   setYearKey]   = useState('year');

  // ── Products ───────────────────────────────────────────────────────────────
  const [products,  setProducts]  = useState<FitmentProduct[]>([]);
  const [loading,   setLoading]   = useState(false);
  const [loaded,    setLoaded]    = useState(false);

  // edits: productId → { make, model, year, aiGenerated }
  const [edits, setEdits] = useState<Map<string, FitmentEdit>>(new Map());
  // editingIds: catalogued products currently in inline-edit mode
  const [editingIds, setEditingIds] = useState<Set<string>>(new Set());

  // ── Selection ──────────────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [copied, setCopied] = useState<{ make: string; model: string; year: string }>({ make: '', model: '', year: '' });
  const [copyFlash, setCopyFlash] = useState<{ id: string; field: string } | null>(null);
  const [showAddYear, setShowAddYear] = useState(false);
  const [yearToAdd, setYearToAdd] = useState('');
  const addYearPreview = useMemo(() => {
    if (!showAddYear || !validFitmentYear(yearToAdd)) return null;
    try {
      return { ...planAddFitmentYear(products, selectedIds, edits, yearToAdd), error: '' };
    } catch (err) {
      return { changes: new Map<string, FitmentEdit>(), skipped: 0, error: (err as Error).message };
    }
  }, [showAddYear, yearToAdd, products, selectedIds, edits]);

  // ── Filters ────────────────────────────────────────────────────────────────
  const [search,         setSearch]         = useState('');
  const [statusFilter,     setStatusFilter]     = useState<StatusFilter>('all');
  const [confidenceFilter, setConfidenceFilter] = useState<ConfidenceFilter>('all');
  const [vendorFilter,     setVendorFilter]     = useState('');
  const [excludeTypes,     setExcludeTypes]     = useState<Set<string>>(new Set());
  const [showExcludeMenu,  setShowExcludeMenu]  = useState(false);
  const [publishFilter,    setPublishFilter]    = useState<'all' | 'ACTIVE' | 'DRAFT'>('all');
  const excludeMenuRef = useRef<HTMLDivElement>(null);

  const toggleExcludeType = useCallback((type: string) => {
    setExcludeTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type); else next.add(type);
      return next;
    });
  }, []);

  // Close exclude menu on outside click
  useEffect(() => {
    if (!showExcludeMenu) return;
    const handler = (e: MouseEvent) => {
      if (excludeMenuRef.current && !excludeMenuRef.current.contains(e.target as Node)) {
        setShowExcludeMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showExcludeMenu]);

  // ── A1 Fitment access ─────────────────────────────────────────────────────
  const [pageView,       setPageView]       = useState<'main' | 'a1fitment'>('main');
  const [a1AccessAllowed, setA1AccessAllowed] = useState(false);

  useEffect(() => {
    if (!token) return;
    fetch(`${BASE}/api/a1-fitment/access-check`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => { if (d.allowed) setA1AccessAllowed(true); })
      .catch(() => {});
  }, [token]);

  // ── ORI Fitment access ────────────────────────────────────────────────────
  const [oriAccessAllowed, setOriAccessAllowed] = useState(false);
  useEffect(() => {
    if (!token) return;
    fetch(`${BASE}/api/fitment/ori-access`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (d.allowed) setOriAccessAllowed(true); })
      .catch(() => {});
  }, [token]);

  // ── Generation ────────────────────────────────────────────────────────────
  const [generating,    setGenerating]    = useState(false);
  const [genProgress,   setGenProgress]   = useState({ current: 0, total: 0 });
  const [generatingOri, setGeneratingOri] = useState(false);
  const [genOriProgress, setGenOriProgress] = useState({ phase: '', current: 0, total: 0 });

  // ── Apply ─────────────────────────────────────────────────────────────────
  const [applying,     setApplying]     = useState(false);
  const [applyResult,  setApplyResult]  = useState<{ ok: boolean; message: string } | null>(null);
  const [genError,     setGenError]     = useState<string | null>(null);

  // ── Model Cleanup Panel ────────────────────────────────────────────────────
  const [showModelCleanup,  setShowModelCleanup]  = useState(false);
  const [expandedMakes,       setExpandedMakes]       = useState<Set<string>>(new Set());
  const [expandedModelYears,  setExpandedModelYears]  = useState<Set<string>>(new Set());
  const [selectedModelKeys,   setSelectedModelKeys]   = useState<Set<string>>(new Set());
  const [cleanupYearFilter,   setCleanupYearFilter]   = useState<'all' | 'has-gaps' | 'no-years'>('all');
  const [cleanupMode,       setCleanupMode]       = useState<'rename' | 'split' | 'remove' | 'assign' | 'hide'>('rename');
  const [renameValue,       setRenameValue]       = useState('');
  const [splitValue,        setSplitValue]        = useState('');
  const [assignMakeValue,   setAssignMakeValue]   = useState('');
  const [selectedMakeKeys,  setSelectedMakeKeys]  = useState<Set<string>>(new Set());

  // ── Clear fitment data ─────────────────────────────────────────────────────
  const [showClearConfirm,  setShowClearConfirm]  = useState(false);
  const [clearConfirmText,  setClearConfirmText]  = useState('');
  const [clearingFitment,   setClearingFitment]   = useState(false);

  // ── Hidden models (hide mode) ──────────────────────────────────────────────
  // Map key: "make::model", value: DB row id
  const [hiddenModels,  setHiddenModels]  = useState<Map<string, number>>(new Map());
  // Map key: make name, value: DB row id
  const [hiddenMakes,   setHiddenMakes]   = useState<Map<string, number>>(new Map());
  const [hiddenLoading, setHiddenLoading] = useState(false);

  useEffect(() => {
    if (!showModelCleanup || !selectedStoreId || !token) return;
    setHiddenLoading(true);
    Promise.all([
      fetch(`${BASE}/api/fitment/hidden-models?storeId=${selectedStoreId}`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.json()),
      fetch(`${BASE}/api/fitment/hidden-makes?storeId=${selectedStoreId}`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.json()),
    ])
      .then(([modelItems, makeItems]: [{ id: number; make: string; model: string }[], { id: number; make: string }[]]) => {
        const modelMap = new Map<string, number>();
        for (const item of modelItems) modelMap.set(`${item.make}::${item.model}`, item.id);
        setHiddenModels(modelMap);
        const makeMap = new Map<string, number>();
        for (const item of makeItems) makeMap.set(item.make, item.id);
        setHiddenMakes(makeMap);
      })
      .catch(() => {})
      .finally(() => setHiddenLoading(false));
  }, [showModelCleanup, selectedStoreId, token]);

  const toggleHiddenModel = useCallback(async (make: string, model: string) => {
    const key = `${make}::${model}`;
    const existingId = hiddenModels.get(key);
    if (existingId !== undefined) {
      setHiddenModels(prev => { const next = new Map(prev); next.delete(key); return next; });
      try {
        await fetch(`${BASE}/api/fitment/hidden-models/${existingId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        setHiddenModels(prev => { const next = new Map(prev); next.set(key, existingId); return next; });
        toast({ title: 'Failed to unhide model', variant: 'destructive' });
      }
    } else {
      setHiddenModels(prev => { const next = new Map(prev); next.set(key, -1); return next; });
      try {
        const r = await fetch(`${BASE}/api/fitment/hidden-models`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ storeId: selectedStoreId, make, model }),
        });
        const data = await r.json();
        setHiddenModels(prev => { const next = new Map(prev); next.set(key, data.id ?? -1); return next; });
      } catch {
        setHiddenModels(prev => { const next = new Map(prev); next.delete(key); return next; });
        toast({ title: 'Failed to hide model', variant: 'destructive' });
      }
    }
  }, [hiddenModels, selectedStoreId, token, toast]);

  const toggleHiddenMake = useCallback(async (make: string) => {
    const existingId = hiddenMakes.get(make);
    if (existingId !== undefined) {
      setHiddenMakes(prev => { const next = new Map(prev); next.delete(make); return next; });
      try {
        await fetch(`${BASE}/api/fitment/hidden-makes/${existingId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        setHiddenMakes(prev => { const next = new Map(prev); next.set(make, existingId); return next; });
        toast({ title: 'Failed to unhide make', variant: 'destructive' });
      }
    } else {
      setHiddenMakes(prev => { const next = new Map(prev); next.set(make, -1); return next; });
      try {
        const r = await fetch(`${BASE}/api/fitment/hidden-makes`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ storeId: selectedStoreId, make }),
        });
        const data = await r.json();
        setHiddenMakes(prev => { const next = new Map(prev); next.set(make, data.id ?? -1); return next; });
      } catch {
        setHiddenMakes(prev => { const next = new Map(prev); next.delete(make); return next; });
        toast({ title: 'Failed to hide make', variant: 'destructive' });
      }
    }
  }, [hiddenMakes, selectedStoreId, token, toast]);

  const makeModelTree = useMemo(() => {
    const tree = new Map<string, Map<string, string[]>>();
    for (const p of products) {
      const edit   = edits.get(p.id);
      const makes  = parseTagArray(edit?.make  ?? p.make  ?? '');
      const models = parseTagArray(edit?.model ?? p.model ?? '');
      const entries = models.length ? models : [''];
      for (const model of entries) {
        const make = resolveModelMake(model, makes);
        if (!tree.has(make)) tree.set(make, new Map());
        const modelMap = tree.get(make)!;
        if (!modelMap.has(model)) modelMap.set(model, []);
        modelMap.get(model)!.push(p.id);
      }
    }
    return new Map(
      [...tree.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([make, models]) => [
          make,
          new Map([...models.entries()].sort(([a], [b]) => a.localeCompare(b))),
        ])
    );
  }, [products, edits]);

  // Map make → model → sorted year numbers (union across all products for that model).
  // Used by the year coverage viewer in the Model Cleanup panel.
  const makeModelYears = useMemo(() => {
    const tree = new Map<string, Map<string, Set<number>>>();
    for (const p of products) {
      const edit     = edits.get(p.id);
      const makes    = parseTagArray(edit?.make  ?? p.make  ?? '');
      const models   = parseTagArray(edit?.model ?? p.model ?? '');
      const years    = parseTagArray(edit?.year  ?? p.year  ?? '');
      const yearNums = years.map(y => parseInt(y, 10)).filter(y => !isNaN(y));
      const entries  = models.length ? models : [''];
      for (const model of entries) {
        const make = resolveModelMake(model, makes);
        if (!tree.has(make)) tree.set(make, new Map());
        const modelMap = tree.get(make)!;
        if (!modelMap.has(model)) modelMap.set(model, new Set());
        const existing = modelMap.get(model)!;
        for (const y of yearNums) {
          existing.add(y);
        }
      }
    }
    const result = new Map<string, Map<string, number[]>>();
    for (const [make, modelMap] of tree.entries()) {
      const sortedModelMap = new Map<string, number[]>();
      for (const [model, yearSet] of modelMap.entries()) {
        sortedModelMap.set(model, [...yearSet].sort((a, b) => a - b));
      }
      result.set(make, sortedModelMap);
    }
    return result;
  }, [products, edits]);

  // Filtered subset of makeModelTree based on cleanupYearFilter.
  const filteredMakeModelTree = useMemo(() => {
    if (cleanupYearFilter === 'all') return makeModelTree;
    const result = new Map<string, Map<string, string[]>>();
    for (const [make, modelMap] of makeModelTree.entries()) {
      const filteredModelMap = new Map<string, string[]>();
      for (const [model, productIds] of modelMap.entries()) {
        const years = makeModelYears.get(make)?.get(model) ?? [];
        if (cleanupYearFilter === 'no-years') {
          if (years.length === 0) filteredModelMap.set(model, productIds);
        } else {
          // has-gaps: at least one consecutive pair with gap > 1
          const hasGap = years.some((y, i) => i > 0 && y - years[i - 1] > 1);
          if (hasGap) filteredModelMap.set(model, productIds);
        }
      }
      if (filteredModelMap.size > 0) result.set(make, filteredModelMap);
    }
    return result;
  }, [makeModelTree, makeModelYears, cleanupYearFilter]);

  // All unique sorted makes available as picker options
  const allMakes = useMemo(
    () => [...makeModelTree.keys()].filter(Boolean).sort((a, b) => a.localeCompare(b)),
    [makeModelTree],
  );

  // All unique years across all products, newest-first
  const allYears = useMemo(() => {
    const yearSet = new Set<string>();
    for (const modelMap of makeModelYears.values()) {
      for (const years of modelMap.values()) {
        for (const y of years) yearSet.add(String(y));
      }
    }
    return [...yearSet].sort((a, b) => parseInt(b, 10) - parseInt(a, 10));
  }, [makeModelYears]);

  const applyModelRename = useCallback(() => {
    const newName = renameValue.trim();
    if (!newName || selectedModelKeys.size === 0) return;
    let affected = 0;
    setEdits(prev => {
      const next = new Map(prev);
      for (const p of products) {
        const edit   = prev.get(p.id);
        const makes  = parseTagArray(edit?.make  ?? p.make  ?? '');
        const models = parseTagArray(edit?.model ?? p.model ?? '');
        let changed  = false;
        const updated = models.map(m => {
          const key = `${resolveModelMake(m, makes)}::${m}`;
          if (selectedModelKeys.has(key)) { changed = true; return newName; }
          return m;
        });
        if (changed) {
          affected++;
          const deduped = [...new Set(updated)].filter(Boolean);
          next.set(p.id, {
            make:        edit?.make  ?? p.make  ?? '',
            model:       JSON.stringify(deduped),
            year:        edit?.year  ?? p.year  ?? '',
            aiGenerated: false,
          });
        }
      }
      return next;
    });
    setSelectedModelKeys(new Set());
    setRenameValue('');
    toast({ title: `Renamed to "${newName}"`, description: `${affected} product${affected !== 1 ? 's' : ''} updated — push to Shopify to save.` });
  }, [renameValue, selectedModelKeys, products, toast]);

  const applyModelSplit = useCallback(() => {
    const splitNames = splitValue.split('\n').map(s => s.trim()).filter(Boolean);
    if (splitNames.length < 2 || selectedModelKeys.size === 0) return;
    let affected = 0;
    setEdits(prev => {
      const next = new Map(prev);
      for (const p of products) {
        const edit   = prev.get(p.id);
        const makes  = parseTagArray(edit?.make  ?? p.make  ?? '');
        const models = parseTagArray(edit?.model ?? p.model ?? '');
        let changed  = false;
        const updated: string[] = [];
        for (const m of models) {
          const key = `${resolveModelMake(m, makes)}::${m}`;
          if (selectedModelKeys.has(key)) { changed = true; updated.push(...splitNames); }
          else updated.push(m);
        }
        if (changed) {
          affected++;
          const deduped = [...new Set(updated)].filter(Boolean);
          next.set(p.id, {
            make:        edit?.make  ?? p.make  ?? '',
            model:       JSON.stringify(deduped),
            year:        edit?.year  ?? p.year  ?? '',
            aiGenerated: false,
          });
        }
      }
      return next;
    });
    setSelectedModelKeys(new Set());
    setSplitValue('');
    toast({ title: `Split into ${splitNames.length} values`, description: `${affected} product${affected !== 1 ? 's' : ''} updated — push to Shopify to save.` });
  }, [splitValue, selectedModelKeys, products, toast]);

  const applyModelRemove = useCallback(() => {
    if (selectedModelKeys.size === 0 && selectedMakeKeys.size === 0) return;
    let affected = 0;
    setEdits(prev => {
      const next = new Map(prev);
      for (const p of products) {
        const edit   = prev.get(p.id);
        const makes  = parseTagArray(edit?.make  ?? p.make  ?? '');
        const models = parseTagArray(edit?.model ?? p.model ?? '');
        // Remove models that are individually selected OR belong to a selected make
        const newModels = models.filter(m => {
          const modelMake = resolveModelMake(m, makes);
          return !selectedMakeKeys.has(modelMake) && !selectedModelKeys.has(`${modelMake}::${m}`);
        });
        // Clean up makes whose models were entirely removed
        const remainingMakeSet = new Set(newModels.map(m => resolveModelMake(m, makes)));
        const newMakes = makes.filter(m => remainingMakeSet.has(m));
        const makeChanged  = newMakes.length !== makes.length;
        const modelChanged = newModels.length !== models.length;
        if (makeChanged || modelChanged) {
          affected++;
          next.set(p.id, {
            make:        JSON.stringify(newMakes),
            model:       JSON.stringify(newModels),
            year:        edit?.year ?? p.year ?? '',
            aiGenerated: false,
          });
        }
      }
      return next;
    });
    setSelectedModelKeys(new Set());
    setSelectedMakeKeys(new Set());
    toast({ title: 'Tags removed', description: `${affected} product${affected !== 1 ? 's' : ''} updated — push to Shopify to save.` });
  }, [selectedModelKeys, selectedMakeKeys, products, toast]);

  const removeAndPush = useCallback(async () => {
    if (selectedModelKeys.size === 0 && selectedMakeKeys.size === 0) return;

    const updates: Array<{ productId: string; make: string; model: string; year: string }> = [];
    for (const p of products) {
      const edit   = edits.get(p.id);
      const makes  = parseTagArray(edit?.make  ?? p.make  ?? '');
      const models = parseTagArray(edit?.model ?? p.model ?? '');
      const newModels = models.filter(m => {
        const modelMake = resolveModelMake(m, makes);
        return !selectedMakeKeys.has(modelMake) && !selectedModelKeys.has(`${modelMake}::${m}`);
      });
      const remainingMakeSet = new Set(newModels.map(m => resolveModelMake(m, makes)));
      const newMakes = makes.filter(m => remainingMakeSet.has(m));
      if (newMakes.length !== makes.length || newModels.length !== models.length) {
        updates.push({
          productId: p.id,
          make:  JSON.stringify(newMakes),
          model: JSON.stringify(newModels),
          year:  edit?.year ?? p.year ?? '',
        });
      }
    }

    if (updates.length === 0) {
      toast({ title: 'No matching products found', variant: 'destructive' });
      return;
    }

    setApplying(true);
    setApplyResult(null);
    try {
      const r = await fetch(`${BASE}/api/fitment/apply`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeId: selectedStoreId, namespace, makeKey, modelKey, yearKey, updates }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error ?? 'Remove failed');
      const errorSuffix = d.errorCount > 0 ? ` (${d.errorCount} failed)` : '';
      setApplyResult({ ok: d.successCount > 0, message: `${d.successCount} product${d.successCount !== 1 ? 's' : ''} updated in Shopify${errorSuffix}` });
      setProducts(prev => prev.map(p => {
        const u = updates.find(u => u.productId === p.id);
        return u ? { ...p, make: u.make, model: u.model, year: u.year } : p;
      }));
      setEdits(prev => {
        const next = new Map(prev);
        updates.forEach(u => next.delete(u.productId));
        return next;
      });
      setSelectedModelKeys(new Set());
      setSelectedMakeKeys(new Set());
      toast({ title: `✓ Tags removed and saved to Shopify` });
    } catch (err: any) {
      setApplyResult({ ok: false, message: err.message ?? 'Remove failed' });
      toast({ title: err.message ?? 'Remove failed', variant: 'destructive' });
    } finally {
      setApplying(false);
    }
  }, [selectedModelKeys, selectedMakeKeys, products, edits, selectedStoreId, namespace, makeKey, modelKey, yearKey, token, toast]);

  const applyAssignMake = useCallback(() => {
    const makeName = assignMakeValue.trim();
    if (!makeName || (selectedModelKeys.size === 0 && selectedMakeKeys.size === 0)) return;
    let affected = 0;
    let partial  = 0;
    setEdits(prev => {
      const next = new Map(prev);
      for (const p of products) {
        const edit   = prev.get(p.id);
        const makes  = parseTagArray(edit?.make  ?? p.make  ?? '');
        const models = parseTagArray(edit?.model ?? p.model ?? '');
        const makeSelected = makes.some(m => selectedMakeKeys.has(m));
        const selectedHere = models.filter(m => selectedModelKeys.has(`${resolveModelMake(m, makes)}::${m}`));
        if (!makeSelected && selectedHere.length === 0) continue;

        // Move the whole product to the new make regardless of how many models
        // were selected. A Shopify product can only have one make field, so
        // there is no way to split a product between two makes — the entire
        // product moves. Unselected models on the same product travel with it,
        // but the user can remove them afterwards if needed.
        affected++;
        next.set(p.id, {
          make:        makeName,
          model:       edit?.model ?? p.model ?? '',
          year:        edit?.year  ?? p.year  ?? '',
          aiGenerated: false,
        });
      }
      return next;
    });
    setSelectedModelKeys(new Set());
    setSelectedMakeKeys(new Set());
    setAssignMakeValue('');
    toast({ title: `Make set to "${makeName}"`, description: `${affected} product${affected !== 1 ? 's' : ''} updated — push to Shopify to save.` });
  }, [assignMakeValue, selectedModelKeys, selectedMakeKeys, products, toast]);

  // ── Custom Code per Store ─────────────────────────────────────────────────
  const [showCustomCode,   setShowCustomCode]   = useState(false);
  const [customCode,       setCustomCode]       = useState('');
  const [savedCode,        setSavedCode]        = useState('');
  const [customCodeSaving, setCustomCodeSaving] = useState(false);
  const [customCodeDirty,  setCustomCodeDirty]  = useState(false);

  // ── Widget Preview ─────────────────────────────────────────────────────────
  const [showPreview,      setShowPreview]      = useState(false);
  const [previewBaseCss,   setPreviewBaseCss]   = useState('');
  const [previewBaseJs,    setPreviewBaseJs]    = useState('');
  const [previewSrcdoc,    setPreviewSrcdoc]    = useState('');
  const [previewLoading,   setPreviewLoading]   = useState(false);
  const previewTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // ── Widget Config ──────────────────────────────────────────────────────────
  const [widgetConfig,        setWidgetConfig]        = useState<WidgetConfig>(DEFAULT_WIDGET_CONFIG);
  const [savedWidgetConfig,   setSavedWidgetConfig]   = useState<WidgetConfig | null>(null);
  const [widgetConfigDirty,   setWidgetConfigDirty]   = useState(false);
  const [widgetConfigSaving,  setWidgetConfigSaving]  = useState(false);
  const [productTypes,        setProductTypes]        = useState<string[]>([]);
  const [productTypesLoading, setProductTypesLoading] = useState(false);

  // ── Push to Theme ──────────────────────────────────────────────────────────
  const [pushingToTheme,    setPushingToTheme]    = useState(false);
  const [pushingJsOnly,     setPushingJsOnly]     = useState(false);
  const [themeResult,       setThemeResult]       = useState<{ ok: boolean; message: string; editorUrl?: string; themeName?: string } | null>(null);
  const [showThemePicker,   setShowThemePicker]   = useState(false);
  const [themesLoading,     setThemesLoading]     = useState(false);
  const [availableThemes,   setAvailableThemes]   = useState<{ id: number; name: string; role: string; updatedAt: string }[]>([]);
  const [pickedThemeId,     setPickedThemeId]     = useState<number | null>(null);

  // ── CSV Export ─────────────────────────────────────────────────────────────
  const [exportingCsv, setExportingCsv] = useState(false);

  // ── CSV Import ─────────────────────────────────────────────────────────────
  const [showImportModal,  setShowImportModal]  = useState(false);
  const [importCsvText,    setImportCsvText]    = useState('');
  const [importFilename,   setImportFilename]   = useState('');
  const [importHeaders,    setImportHeaders]    = useState<string[]>([]);
  const [importSampleRows, setImportSampleRows] = useState<Record<string, string>[]>([]);
  const [importRunning,    setImportRunning]    = useState(false);
  const [importProgress,   setImportProgress]   = useState({ current: 0, total: 0 });
  const [importDone,       setImportDone]       = useState<{ total: number; notFound: number; errors: number; notFoundHandles: string[] } | null>(null);
  const [importError,      setImportError]      = useState<string | null>(null);
  const importFileRef = useRef<HTMLInputElement>(null);

  const pushToTheme = useCallback(async (themeId: number) => {
    if (!selectedStoreId) { toast({ title: 'Select a store first', variant: 'destructive' }); return; }
    setShowThemePicker(false);
    setPushingToTheme(true);
    setThemeResult(null);
    try {
      const r = await fetch(`${BASE}/api/fitment/push-to-theme`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeId: selectedStoreId, themeId, namespace, makeKey, modelKey, yearKey, collectionHandle: 'all' }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error ?? 'Push failed');
      setThemeResult({ ok: true, message: d.message, editorUrl: d.editorUrl, themeName: d.themeName });
    } catch (err: any) {
      setThemeResult({ ok: false, message: err.message ?? 'Failed to push section to theme' });
      toast({ title: err.message ?? 'Push failed', variant: 'destructive' });
    } finally {
      setPushingToTheme(false);
    }
  }, [selectedStoreId, namespace, makeKey, modelKey, yearKey, token, toast]);

  const pushJsOnly = useCallback(async (themeId: number) => {
    if (!selectedStoreId) { toast({ title: 'Select a store first', variant: 'destructive' }); return; }
    setShowThemePicker(false);
    setPushingJsOnly(true);
    setThemeResult(null);
    try {
      const r = await fetch(`${BASE}/api/fitment/push-js-only`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeId: selectedStoreId, themeId }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error ?? 'Push failed');
      setThemeResult({ ok: true, message: d.message, themeName: d.themeName });
    } catch (err: any) {
      setThemeResult({ ok: false, message: err.message ?? 'Failed to push JS to theme' });
      toast({ title: err.message ?? 'JS push failed', variant: 'destructive' });
    } finally {
      setPushingJsOnly(false);
    }
  }, [selectedStoreId, token, toast]);

  const openThemePicker = useCallback(async () => {
    if (!selectedStoreId) { toast({ title: 'Select a store first', variant: 'destructive' }); return; }
    setThemesLoading(true);
    setAvailableThemes([]);
    setPickedThemeId(null);
    setShowThemePicker(true);
    try {
      const r = await fetch(`${BASE}/api/fitment/themes?storeId=${selectedStoreId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error ?? 'Failed to load themes');
      const themes = d.themes ?? [];
      setAvailableThemes(themes);
      const live = themes.find((t: any) => t.role === 'main');
      if (live) setPickedThemeId(live.id);
    } catch (err: any) {
      toast({ title: err.message ?? 'Failed to load themes', variant: 'destructive' });
      setShowThemePicker(false);
    } finally {
      setThemesLoading(false);
    }
  }, [selectedStoreId, token, toast]);

  // ── CSV Export ─────────────────────────────────────────────────────────────
  const exportCsv = useCallback(async () => {
    if (!selectedStoreId) { toast({ title: 'Select a store first', variant: 'destructive' }); return; }
    setExportingCsv(true);
    try {
      const params = new URLSearchParams({ storeId: selectedStoreId, namespace, makeKey, modelKey, yearKey });
      const r = await fetch(`${BASE}/api/fitment/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error ?? 'Export failed'); }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'fitment-export.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      toast({ title: err.message ?? 'Export failed', variant: 'destructive' });
    } finally {
      setExportingCsv(false);
    }
  }, [selectedStoreId, namespace, makeKey, modelKey, yearKey, token, toast]);

  // ── CSV Import ─────────────────────────────────────────────────────────────
  const handleImportFile = useCallback((file: File) => {
    setImportDone(null);
    setImportError(null);
    setImportProgress({ current: 0, total: 0 });
    setImportFilename(file.name);
    const reader = new FileReader();
    reader.onload = e => {
      const text = (e.target?.result ?? '') as string;
      setImportCsvText(text);
      // Parse headers + sample rows in the browser for preview
      const lines = text.split('\n').filter(Boolean);
      if (lines.length === 0) { setImportHeaders([]); setImportSampleRows([]); return; }
      const parseRow = (line: string) => {
        const cells: string[] = [];
        let cur = ''; let inQ = false;
        for (let i = 0; i < line.length; i++) {
          const c = line[i]!;
          if (c === '"') { inQ = !inQ; }
          else if (c === ',' && !inQ) { cells.push(cur.trim()); cur = ''; }
          else { cur += c; }
        }
        cells.push(cur.trim());
        return cells;
      };
      const headers = parseRow(lines[0]!);
      const sampleRows = lines.slice(1, 6).map(l => {
        const vals = parseRow(l);
        return Object.fromEntries(headers.map((h, i) => [h, vals[i] ?? '']));
      });
      setImportHeaders(headers);
      setImportSampleRows(sampleRows);
    };
    reader.readAsText(file);
  }, []);

  // ── Custom Code callbacks ─────────────────────────────────────────────────
  const loadCustomCode = useCallback(async (storeId: string) => {
    if (!storeId || !token) return;
    try {
      const r = await fetch(`${BASE}/api/fitment/custom-code?storeId=${storeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      const c = d.code ?? '';
      setCustomCode(c);
      setSavedCode(c);
      setCustomCodeDirty(false);
      const wc = d.widgetConfig as WidgetConfig | null;
      const loaded = wc ? { ...DEFAULT_WIDGET_CONFIG, ...wc } : DEFAULT_WIDGET_CONFIG;
      setWidgetConfig(loaded);
      setSavedWidgetConfig(wc);
      setWidgetConfigDirty(false);
    } catch { /* silently ignore */ }
  }, [token]);

  const saveWidgetConfig = useCallback(async () => {
    if (!selectedStoreId) return;
    setWidgetConfigSaving(true);
    try {
      const r = await fetch(`${BASE}/api/fitment/custom-code`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeId: selectedStoreId, widgetConfig }),
      });
      if (!r.ok) throw new Error((await r.json()).error ?? 'Save failed');
      setSavedWidgetConfig(widgetConfig);
      setWidgetConfigDirty(false);
      toast({ title: 'Widget settings saved' });
    } catch (err: any) {
      toast({ title: err.message ?? 'Save failed', variant: 'destructive' });
    } finally {
      setWidgetConfigSaving(false);
    }
  }, [selectedStoreId, widgetConfig, token, toast]);

  const loadProductTypes = useCallback(async () => {
    if (!selectedStoreId || !token) return;
    setProductTypesLoading(true);
    try {
      const r = await fetch(`${BASE}/api/fitment/product-types?storeId=${selectedStoreId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error((await r.json()).error ?? 'Failed');
      const d = await r.json();
      setProductTypes(d.productTypes ?? []);
    } catch (err: any) {
      toast({ title: err.message ?? 'Failed to load product types', variant: 'destructive' });
    } finally {
      setProductTypesLoading(false);
    }
  }, [selectedStoreId, token, toast]);

  const saveCustomCode = useCallback(async () => {
    if (!selectedStoreId) return;
    setCustomCodeSaving(true);
    try {
      const r = await fetch(`${BASE}/api/fitment/custom-code`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeId: selectedStoreId, code: customCode }),
      });
      if (!r.ok) throw new Error((await r.json()).error ?? 'Save failed');
      setSavedCode(customCode);
      setCustomCodeDirty(false);
      toast({ title: 'Custom code saved' });
    } catch (err: any) {
      toast({ title: err.message ?? 'Save failed', variant: 'destructive' });
    } finally {
      setCustomCodeSaving(false);
    }
  }, [selectedStoreId, customCode, token, toast]);

  useEffect(() => {
    if (selectedStoreId) loadCustomCode(selectedStoreId);
  }, [selectedStoreId, loadCustomCode]);

  // ── Preview: load base assets once, then rebuild srcdoc on CSS changes ───────
  const loadPreviewAssets = useCallback(async () => {
    if (!token) return;
    setPreviewLoading(true);
    try {
      const r = await fetch(`${BASE}/api/fitment/preview-assets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Failed to load preview assets');
      const d = await r.json();
      const css = d.baseCss ?? '';
      const js  = d.baseJs  ?? '';
      setPreviewBaseCss(css);
      setPreviewBaseJs(js);
      setPreviewSrcdoc(buildPreviewSrcdoc(css, customCode, js, widgetConfig));
    } catch { /* silently ignore */ }
    finally { setPreviewLoading(false); }
  }, [token, customCode, widgetConfig]);

  // Open preview → load assets (once)
  useEffect(() => {
    if (showPreview && !previewBaseJs && !previewLoading) {
      loadPreviewAssets();
    }
  }, [showPreview, previewBaseJs, previewLoading, loadPreviewAssets]);

  // Custom CSS / widgetConfig changes → debounced srcdoc rebuild
  useEffect(() => {
    if (!previewBaseJs || !showPreview) return;
    clearTimeout(previewTimerRef.current);
    previewTimerRef.current = setTimeout(() => {
      setPreviewSrcdoc(buildPreviewSrcdoc(previewBaseCss, customCode, previewBaseJs, widgetConfig));
    }, 300);
    return () => clearTimeout(previewTimerRef.current);
  }, [customCode, previewBaseCss, previewBaseJs, showPreview, widgetConfig]);

  // ── Derived lists ──────────────────────────────────────────────────────────
  const uniqueVendors = useMemo(() =>
    [...new Set(products.map(p => p.vendor).filter(Boolean))].sort(),
    [products]);

  const uniqueTypes = useMemo(() =>
    [...new Set(products.map(p => p.productType).filter(Boolean))].sort(),
    [products]);

  const filteredProducts = useMemo(() => {
    let list = products;

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        p.title.toLowerCase().includes(q) ||
        p.handle.toLowerCase().includes(q) ||
        p.vendor.toLowerCase().includes(q)
      );
    }

    if (statusFilter === 'catalogued') {
      list = list.filter(p => getStatus(p, edits) === 'catalogued');
    } else if (statusFilter === 'uncatalogued') {
      list = list.filter(p => getStatus(p, edits) !== 'catalogued');
    } else if (statusFilter === 'supplier-data') {
      list = list.filter(p => getStatus(p, edits) === 'ori');
    }

    if (vendorFilter)          list = list.filter(p => p.vendor === vendorFilter);
    if (excludeTypes.size > 0) list = list.filter(p => !excludeTypes.has(p.productType));
    if (publishFilter !== 'all') list = list.filter(p => p.publishedStatus === publishFilter);

    if (confidenceFilter !== 'all') {
      list = list.filter(p => {
        const e = edits.get(p.id);
        return e?.aiGenerated && e?.confidence === confidenceFilter;
      });
    }

    return list;
  }, [products, search, statusFilter, confidenceFilter, vendorFilter, excludeTypes, publishFilter, edits]);

  const stats = useMemo(() => {
    const catalogued   = products.filter(p => getStatus(p, edits) === 'catalogued').length;
    const aiGenerated  = products.filter(p => getStatus(p, edits) === 'ai').length;
    const pending      = products.filter(p => getStatus(p, edits) === 'pending').length;
    return { total: products.length, catalogued, aiGenerated, pending };
  }, [products, edits]);

  // ── Pagination ─────────────────────────────────────────────────────────────
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Reset to page 1 whenever filters change
  useEffect(() => { setPage(1); }, [search, statusFilter, confidenceFilter, vendorFilter, excludeTypes, publishFilter]);

  const totalPages      = Math.max(1, Math.ceil(filteredProducts.length / pageSize));
  const safePage        = Math.min(page, totalPages);
  const paginatedProducts = filteredProducts.slice((safePage - 1) * pageSize, safePage * pageSize);

  const visibleSelected = useMemo(
    () => paginatedProducts.filter(p => selectedIds.has(p.id)),
    [paginatedProducts, selectedIds]
  );

  const allVisibleSelected    = paginatedProducts.length > 0 && paginatedProducts.every(p => selectedIds.has(p.id));
  const allFilteredSelected   = filteredProducts.length > 0  && filteredProducts.every(p => selectedIds.has(p.id));
  const someNotOnPageSelected = filteredProducts.length > paginatedProducts.length && allVisibleSelected && !allFilteredSelected;

  // ── Actions ────────────────────────────────────────────────────────────────

  const loadProducts = useCallback(async () => {
    if (!selectedStoreId) { toast({ title: 'Select a store first', variant: 'destructive' }); return; }
    setLoading(true);
    setLoaded(false);
    setProducts([]);
    setEdits(new Map());
    setSelectedIds(new Set());
    setApplyResult(null);

    try {
      const params = new URLSearchParams({ storeId: selectedStoreId, namespace, makeKey, modelKey, yearKey });
      const r = await fetch(`${BASE}/api/fitment/products?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error ?? 'Failed to load products');
      setProducts(d.products ?? []);
      setLoaded(true);
      toast({ title: `${(d.products ?? []).length} products loaded` });
    } catch (err: any) {
      toast({ title: err.message ?? 'Failed to load', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [selectedStoreId, namespace, makeKey, modelKey, yearKey, token, toast]);

  const clearFitmentData = useCallback(async () => {
    if (!selectedStoreId) return;
    setClearingFitment(true);
    setShowClearConfirm(false);
    setClearConfirmText('');
    try {
      const r = await fetch(`${BASE}/api/fitment/clear`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeId: selectedStoreId, namespace, makeKey, modelKey, yearKey }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error ?? 'Clear failed');
      toast({ title: `Cleared ${d.successCount} product${d.successCount !== 1 ? 's' : ''}`, description: 'All make, model and year values have been reset.' });
      await loadProducts();
    } catch (err: any) {
      toast({ title: err.message ?? 'Clear failed', variant: 'destructive' });
    } finally {
      setClearingFitment(false);
    }
  }, [selectedStoreId, namespace, makeKey, modelKey, yearKey, token, toast, loadProducts]);

  const runImport = useCallback(async () => {
    if (!selectedStoreId || !importCsvText) return;
    setImportRunning(true);
    setImportDone(null);
    setImportError(null);
    setImportProgress({ current: 0, total: 0 });
    try {
      const r = await fetch(`${BASE}/api/fitment/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeId: selectedStoreId, csvText: importCsvText, namespace, makeKey, modelKey, yearKey }),
      });
      if (!r.ok || !r.body) throw new Error('Stream failed to start');
      const reader = r.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop() ?? '';
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith('data:')) continue;
          try {
            const ev = JSON.parse(line.slice(5).trim());
            if (ev.type === 'progress') setImportProgress({ current: ev.current, total: ev.total });
            else if (ev.type === 'done') {
              setImportDone({ total: ev.total, notFound: ev.notFound ?? 0, errors: ev.errors ?? 0, notFoundHandles: ev.notFoundHandles ?? [] });
              loadProducts();
            } else if (ev.type === 'error') {
              setImportError(ev.message ?? 'Import failed');
            }
          } catch { /* ignore parse errors */ }
        }
      }
    } catch (err: any) {
      setImportError(err.message ?? 'Import failed');
    } finally {
      setImportRunning(false);
    }
  }, [selectedStoreId, importCsvText, namespace, makeKey, modelKey, yearKey, token, loadProducts]);

  const openImportModal = useCallback(() => {
    setShowImportModal(true);
    setImportCsvText('');
    setImportFilename('');
    setImportHeaders([]);
    setImportSampleRows([]);
    setImportDone(null);
    setImportError(null);
    setImportProgress({ current: 0, total: 0 });
    setImportRunning(false);
  }, []);

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allFilteredSelected) {
      // All filtered selected → clear all
      setSelectedIds(new Set());
    } else if (allVisibleSelected) {
      // Page selected but not all filtered → deselect page
      setSelectedIds(prev => {
        const next = new Set(prev);
        paginatedProducts.forEach(p => next.delete(p.id));
        return next;
      });
    } else {
      // Nothing / partial → select current page
      setSelectedIds(prev => {
        const next = new Set(prev);
        paginatedProducts.forEach(p => next.add(p.id));
        return next;
      });
    }
  };

  const selectAllFiltered = () => {
    setSelectedIds(new Set(filteredProducts.map(p => p.id)));
  };

  const selectUncatalogued = () => {
    const uncatalogued = products.filter(p => getStatus(p, edits) !== 'catalogued');
    setSelectedIds(new Set(uncatalogued.map(p => p.id)));
  };

  const copyField = (productId: string, field: 'make' | 'model' | 'year', value: string) => {
    if (!value) return;
    setCopied(prev => ({ ...prev, [field]: value }));
    setCopyFlash({ id: productId, field });
    const text = parseTagArray(value).join(', ');
    navigator.clipboard.writeText(text).catch(() => {});
    setTimeout(() => setCopyFlash(null), 1200);
  };

  const pasteToSelected = (field: 'make' | 'model' | 'year') => {
    const value = copied[field];
    if (!value) return;
    const selected = products.filter(p => selectedIds.has(p.id));
    selected.forEach(p => updateEdit(p, field, value));
  };

  const addYearToSelected = () => {
    if (!addYearPreview || addYearPreview.error || addYearPreview.changes.size === 0) return;
    setEdits(prev => {
      const next = new Map(prev);
      addYearPreview.changes.forEach((edit, id) => next.set(id, edit));
      return next;
    });
    setShowAddYear(false);
    toast({
      title: `${yearToAdd.trim()} added to ${addYearPreview.changes.size} pending product edits`,
      description: 'Review the Year column, then use Apply Selected to save to Shopify.',
    });
  };

  const updateEdit = (product: FitmentProduct, field: 'make' | 'model' | 'year', value: string) => {
    setEdits(prev => {
      const next = new Map(prev);
      // Seed from the product's Shopify values so untouched fields are preserved
      const existing = next.get(product.id) ?? {
        make:        product.make,
        model:       product.model,
        year:        product.year,
        aiGenerated: false,
      };
      next.set(product.id, { ...existing, [field]: value, aiGenerated: false });
      return next;
    });
  };

  // Set all three fields to Universal / clear in one atomic update
  const setUniversalFit = (product: FitmentProduct, on: boolean) => {
    // Ensure the product is in edit mode before mutating
    setEditingIds(prev => { const s = new Set(prev); s.add(product.id); return s; });
    setEdits(prev => {
      const next = new Map(prev);
      const existing = next.get(product.id) ?? {
        make: product.make, model: product.model, year: product.year, aiGenerated: false,
      };
      if (on) {
        next.set(product.id, { ...existing, make: JSON.stringify(['Universal']), model: JSON.stringify(['Universal']), year: '', aiGenerated: false });
      } else {
        next.set(product.id, { ...existing, make: '', model: '', year: '', aiGenerated: false });
      }
      return next;
    });
  };

  // Enter inline-edit mode for a catalogued product, pre-seeding its current values
  const startEditing = (product: FitmentProduct) => {
    setEditingIds(prev => { const s = new Set(prev); s.add(product.id); return s; });
    setEdits(prev => {
      if (prev.has(product.id)) return prev;
      const next = new Map(prev);
      next.set(product.id, { make: product.make, model: product.model, year: product.year, aiGenerated: false });
      return next;
    });
  };

  // ── Generate fitment from ORI catalogue ───────────────────────────────────
  const generateFromOri = useCallback(async () => {
    const toGenerate = products.filter(p => selectedIds.has(p.id));
    if (toGenerate.length === 0) { toast({ title: 'Select products first', variant: 'destructive' }); return; }

    setGeneratingOri(true);
    setGenOriProgress({ phase: 'downloading_ori', current: 0, total: toGenerate.length });
    setApplyResult(null);
    setGenError(null);

    try {
      const resp = await fetch(`${BASE}/api/fitment/generate-from-ori`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          storeId: selectedStoreId,
          force: true,
          products: toGenerate.map(p => {
            const edit = edits.get(p.id);
            return {
              id:            p.id,
              title:         p.title,
              bodyHtml:      p.bodyHtml,
              vendor:        p.vendor,
              productType:   p.productType,
              tags:          p.tags,
              sku:           p.sku,
              existingMake:  edit?.make  ?? p.make,
              existingModel: edit?.model ?? p.model,
              existingYear:  edit?.year  ?? p.year,
            };
          }),
          catalogue: (() => {
            const entries: { make: string; model: string }[] = [];
            for (const p of products) {
              const edit   = edits.get(p.id);
              const makes  = parseTagArray(edit?.make  ?? p.make  ?? '');
              const models = parseTagArray(edit?.model ?? p.model ?? '');
              const make   = makes[0] ?? '';
              if (models.length) for (const model of models) entries.push({ make, model });
              else if (make) entries.push({ make, model: '' });
            }
            return entries;
          })(),
        }),
      });

      if (!resp.ok || !resp.body) {
        const errJson = resp.ok ? {} : await resp.json().catch(() => ({}));
        throw new Error((errJson as any).error ?? 'ORI stream failed to start');
      }

      const reader  = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      let resultCount = 0;
      let oriMatchCount = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop() ?? '';

        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith('data:')) continue;
          try {
            const ev = JSON.parse(line.slice(5).trim());
            if (ev.type === 'progress') {
              setGenOriProgress({ phase: ev.phase ?? 'generating', current: ev.current, total: ev.total });
            } else if (ev.type === 'result') {
              resultCount++;
              if (ev.oriMatched) oriMatchCount++;
              setEdits(prev => {
                const next = new Map(prev);
                // AI result always wins — force=true means we re-ran intentionally.
                // Parse fallback values through parseTagArray so corrupt [""] entries
                // from Shopify don't silently mask empty AI results.
                const product  = products.find(p => p.id === ev.productId);
                const existing = next.get(ev.productId);
                const cleanVal = (raw?: string) => parseTagArray(raw ?? '').join(', ');
                next.set(ev.productId, {
                  make:        ev.make  || cleanVal(existing?.make)  || cleanVal(product?.make)  || '',
                  model:       ev.model || cleanVal(existing?.model) || cleanVal(product?.model) || '',
                  year:        ev.year  || cleanVal(existing?.year)  || cleanVal(product?.year)  || '',
                  aiGenerated: true,
                  confidence:  ev.confidence as 'high' | 'medium' | 'low' | undefined,
                  source:      ev.oriMatched ? 'ori' : 'ai',
                });
                return next;
              });
              setGenOriProgress(p => ({ ...p, current: ev.current, total: ev.total }));
            } else if (ev.type === 'error') {
              toast({ title: `ORI fitment error: ${ev.message ?? 'Generation error'}`, variant: 'destructive' });
            }
          } catch { /* malformed line */ }
        }
      }

      if (resultCount > 0) {
        toast({ title: `ORI fitment generated for ${resultCount} product${resultCount !== 1 ? 's' : ''}`, description: oriMatchCount > 0 ? `${oriMatchCount} matched ORI catalogue data` : 'No SKU matches found — AI used Shopify data only' });
      } else {
        setGenError('ORI generation ran but returned no results.');
      }
    } catch (err: any) {
      const msg = err.message ?? 'ORI generation failed';
      setGenError(`ORI generation failed: ${msg}`);
      toast({ title: msg, variant: 'destructive' });
    } finally {
      setGeneratingOri(false);
      setGenOriProgress({ phase: '', current: 0, total: 0 });
    }
  }, [products, selectedIds, selectedStoreId, token, toast, edits]);

  const generateDescriptions = useCallback(async () => {
    const toGenerate = products.filter(p => selectedIds.has(p.id));
    if (toGenerate.length === 0) { toast({ title: 'Select products first', variant: 'destructive' }); return; }

    setGenerating(true);
    setGenProgress({ current: 0, total: toGenerate.length });
    setApplyResult(null);
    setGenError(null);

    try {
      const resp = await fetch(`${BASE}/api/fitment/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          storeId: selectedStoreId,
          force: true,
          products: toGenerate.map(p => {
            const edit = edits.get(p.id);
            return {
              id:            p.id,
              title:         p.title,
              bodyHtml:      p.bodyHtml,
              vendor:        p.vendor,
              productType:   p.productType,
              tags:          p.tags,
              existingMake:  edit?.make  ?? p.make,
              existingModel: edit?.model ?? p.model,
              existingYear:  edit?.year  ?? p.year,
            };
          }),
          catalogue: (() => {
            const entries: { make: string; model: string }[] = [];
            for (const p of products) {
              const edit  = edits.get(p.id);
              const makes  = parseTagArray(edit?.make  ?? p.make  ?? '');
              const models = parseTagArray(edit?.model ?? p.model ?? '');
              const make   = makes[0] ?? '';
              if (models.length) {
                for (const model of models) entries.push({ make, model });
              } else if (make) {
                entries.push({ make, model: '' });
              }
            }
            return entries;
          })(),
        }),
      });

      if (!resp.ok || !resp.body) throw new Error('Stream failed to start');

      const reader  = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      let resultCount = 0;
      let lastError = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop() ?? '';

        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith('data:')) continue;
          try {
            const ev = JSON.parse(line.slice(5).trim());
            if (ev.type === 'progress') {
              setGenProgress({ current: ev.current, total: ev.total });
            } else if (ev.type === 'result') {
              resultCount++;
              setEdits(prev => {
                const next = new Map(prev);
                // AI result always wins — force=true means we re-ran intentionally.
                // Parse fallback values through parseTagArray so corrupt [""] entries
                // from Shopify don't silently mask empty AI results.
                const product  = products.find(p => p.id === ev.productId);
                const existing = next.get(ev.productId);
                const cleanVal = (raw?: string) => parseTagArray(raw ?? '').join(', ');
                next.set(ev.productId, {
                  make:        ev.make  || cleanVal(existing?.make)  || cleanVal(product?.make)  || '',
                  model:       ev.model || cleanVal(existing?.model) || cleanVal(product?.model) || '',
                  year:        ev.year  || cleanVal(existing?.year)  || cleanVal(product?.year)  || '',
                  aiGenerated: true,
                  confidence:  ev.confidence as 'high' | 'medium' | 'low' | undefined,
                });
                return next;
              });
              setGenProgress({ current: ev.current, total: ev.total });
            } else if (ev.type === 'error') {
              lastError = ev.message ?? 'Generation error';
              toast({ title: `AI error: ${lastError}`, variant: 'destructive' });
            }
          } catch {
            // Malformed SSE line — skip silently
          }
        }
      }

      if (resultCount > 0) {
        toast({ title: `AI fitment generated for ${resultCount} product${resultCount !== 1 ? 's' : ''}` });
      } else if (lastError) {
        setGenError(`AI generation failed: ${lastError}`);
      } else {
        setGenError('AI ran but returned no results. Check browser console (F12) for details.');
      }
    } catch (err: any) {
      const msg = err.message ?? 'Generation failed';
      setGenError(`AI generation failed: ${msg}`);
      toast({ title: msg, variant: 'destructive' });
    } finally {
      setGenerating(false);
      setGenProgress({ current: 0, total: 0 });
    }
  }, [products, selectedIds, selectedStoreId, token, toast]);

  const applyFitment = useCallback(async () => {
    // Build list of products that have AI-generated or manually edited values
    const toApply = products.filter(p => {
      if (!selectedIds.has(p.id)) return false;
      const e = edits.get(p.id);
      return e && (e.make || e.model || e.year);
    });

    if (toApply.length === 0) {
      toast({ title: 'No pending fitment edits on selected products', variant: 'destructive' });
      return;
    }

    setApplying(true);
    setApplyResult(null);

    try {
      const r = await fetch(`${BASE}/api/fitment/apply`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          storeId: selectedStoreId,
          namespace,
          makeKey,
          modelKey,
          yearKey,
          updates: toApply.map(p => {
            const e = edits.get(p.id)!;
            return { productId: p.id, make: e.make, model: e.model, year: e.year };
          }),
        }),
      });

      const d = await r.json();
      if (!r.ok) throw new Error(d.error ?? 'Apply failed');

      // Surface the actual Shopify error message if any products failed
      const firstError = d.errors?.[0]?.message;
      const errorSuffix = d.errorCount > 0
        ? ` (${d.errorCount} failed${firstError ? `: ${firstError}` : ''})`
        : '';

      setApplyResult({
        ok: d.successCount > 0,
        message: `${d.successCount} product${d.successCount !== 1 ? 's' : ''} updated in Shopify${errorSuffix}`,
      });

      const savedIds = confirmedFitmentIds(toApply.map(p => p.id), d);
      // Merge only confirmed successes; failed edits remain available for retry.
      setProducts(prev => prev.map(p => {
        const e = edits.get(p.id);
        if (!savedIds.has(p.id) || !e) return p;
        return { ...p, make: e.make, model: e.model, year: e.year };
      }));
      // Clear edits for applied products
      setEdits(prev => {
        const next = new Map(prev);
        savedIds.forEach(id => next.delete(id));
        return next;
      });

      toast({ title: `✓ ${d.successCount} product fitments saved to Shopify` });
    } catch (err: any) {
      setApplyResult({ ok: false, message: err.message ?? 'Apply failed' });
      toast({ title: err.message ?? 'Apply failed', variant: 'destructive' });
    } finally {
      setApplying(false);
    }
  }, [products, selectedIds, edits, selectedStoreId, namespace, makeKey, modelKey, yearKey, token, toast]);

  const pushAllEdits = useCallback(async () => {
    const toApply = products.filter(p => {
      const e = edits.get(p.id);
      return e && (e.make || e.model || e.year);
    });
    if (toApply.length === 0) return;

    setApplying(true);
    setApplyResult(null);
    try {
      const r = await fetch(`${BASE}/api/fitment/apply`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          storeId: selectedStoreId,
          namespace, makeKey, modelKey, yearKey,
          updates: toApply.map(p => {
            const e = edits.get(p.id)!;
            return { productId: p.id, make: e.make, model: e.model, year: e.year };
          }),
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error ?? 'Apply failed');
      const firstError = d.errors?.[0]?.message;
      const errorSuffix = d.errorCount > 0
        ? ` (${d.errorCount} failed${firstError ? `: ${firstError}` : ''})`
        : '';
      setApplyResult({
        ok: d.successCount > 0,
        message: `${d.successCount} product${d.successCount !== 1 ? 's' : ''} updated in Shopify${errorSuffix}`,
      });
      const savedIds = confirmedFitmentIds(toApply.map(p => p.id), d);
      setProducts(prev => prev.map(p => {
        const e = edits.get(p.id);
        if (!savedIds.has(p.id) || !e) return p;
        return { ...p, make: e.make, model: e.model, year: e.year };
      }));
      setEdits(prev => {
        const next = new Map(prev);
        savedIds.forEach(id => next.delete(id));
        return next;
      });
      toast({ title: `✓ ${d.successCount} product fitments saved to Shopify` });
    } catch (err: any) {
      setApplyResult({ ok: false, message: err.message ?? 'Apply failed' });
      toast({ title: err.message ?? 'Apply failed', variant: 'destructive' });
    } finally {
      setApplying(false);
    }
  }, [products, edits, selectedStoreId, namespace, makeKey, modelKey, yearKey, token, toast]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="space-y-6">

        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground flex items-center gap-2">
              <Car className="w-6 h-6 text-primary" /> Vehicle Fitment
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Use AI to populate Make, Model and Year metafields from product titles and descriptions.
            </p>
          </div>

          {/* Action buttons */}
          {selectedStoreId && (
            <div className="flex items-center gap-2 flex-wrap self-start">
              <button
                onClick={exportCsv}
                disabled={exportingCsv}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-muted/50 hover:bg-muted border border-border text-foreground text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exportingCsv
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Exporting…</>
                  : <><Download className="w-4 h-4" /> Export CSV</>
                }
              </button>
              <button
                onClick={openImportModal}
                disabled={importRunning}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-muted/50 hover:bg-muted border border-border text-foreground text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Upload className="w-4 h-4" /> Bulk Import
              </button>
              <button
                onClick={openThemePicker}
                disabled={pushingToTheme}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/20 hover:bg-primary/30 border border-primary/30 text-primary text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {pushingToTheme
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Pushing…</>
                  : <><Car className="w-4 h-4" /> Push Filter to Theme</>
                }
              </button>
            </div>
          )}
        </div>

        {/* ── Page View Tabs (A1 access only) ── */}
        {a1AccessAllowed && (
          <div className="flex gap-1 p-1 bg-muted/50 border border-border/50 rounded-xl w-fit">
            <button
              onClick={() => setPageView('main')}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                pageView === 'main'
                  ? 'bg-primary/20 border border-primary/30 text-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <Wand2 className="w-3.5 h-3.5" /> AI Cataloguing
            </button>
            <button
              onClick={() => setPageView('a1fitment')}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                pageView === 'a1fitment'
                  ? 'bg-primary/20 border border-primary/30 text-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <Car className="w-3.5 h-3.5" /> A1 Fitment
            </button>
          </div>
        )}

        {/* ── A1 Fitment Panel ── */}
        {pageView === 'a1fitment' && (
          <A1FitmentSection stores={stores} token={token!} />
        )}

        {/* ── Main cataloguing content (hidden when A1 tab active) ── */}
        {pageView !== 'a1fitment' && (<>

        {/* Theme push result banner */}
        {themeResult && (
          <div className={`flex items-start gap-3 px-4 py-3 rounded-xl border text-sm ${
            themeResult.ok
              ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
              : 'bg-red-500/10 border-red-500/20 text-red-400'
          }`}>
            {themeResult.ok
              ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
              : <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
            }
            <div className="space-y-1 min-w-0">
              <p>{themeResult.message}</p>
              {themeResult.ok && themeResult.editorUrl && (
                <a
                  href={themeResult.editorUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-emerald-700 dark:text-emerald-300 hover:text-foreground font-medium transition-colors"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Open {themeResult.themeName ? `"${themeResult.themeName}"` : 'Theme'} Editor →
                </a>
              )}
            </div>
          </div>
        )}

        {/* Info banner when no store selected */}
        {!selectedStoreId && stores.length > 0 && (
          <div className="flex items-start gap-3 px-4 py-3 bg-primary/5 border border-primary/15 rounded-xl text-sm text-muted-foreground">
            <Car className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <div>
              <span className="text-foreground font-medium">Homepage filter widget available.</span>{' '}
              Select a store above, then click <span className="text-primary font-medium">Push Filter to Theme</span> to instantly inject a Make / Model / Year filter section into your live Shopify theme — no app extension deployment required.
            </div>
          </div>
        )}

        {/* Store + Config bar */}
        <div className="bg-muted/30 border border-border/60 rounded-2xl p-5 space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            {/* Store picker */}
            <ShopifyTargetStoreSelector
              stores={stores}
              value={selectedStoreId}
              onChange={setSelectedStoreId}
              label="Target Shopify store"
              className="flex-1 min-w-[180px]"
            />

            {/* Config toggle */}
            <button
              onClick={() => setShowConfig(v => !v)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                showConfig
                  ? 'bg-primary/10 border-primary/30 text-primary'
                  : 'bg-muted/50 border-border text-muted-foreground hover:text-foreground'
              }`}
            >
              <Settings2 className="w-3.5 h-3.5" />
              Metafield Keys
              {showConfig ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>

            {/* Load button */}
            <button
              onClick={loadProducts}
              disabled={loading || !selectedStoreId}
              className="flex items-center gap-2 px-5 py-2 rounded-xl bg-primary/20 hover:bg-primary/30 border border-primary/30 text-primary text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {loading ? 'Loading…' : loaded ? 'Reload' : 'Load Products'}
            </button>
          </div>

          {/* Config panel */}
          {showConfig && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 border-t border-border/50">
              {[
                { label: 'Namespace', value: namespace, onChange: setNamespace, placeholder: 'custom' },
                { label: 'Make Key',  value: makeKey,   onChange: setMakeKey,   placeholder: 'make'   },
                { label: 'Model Key', value: modelKey,  onChange: setModelKey,  placeholder: 'model'  },
                { label: 'Year Key',  value: yearKey,   onChange: setYearKey,   placeholder: 'year'   },
              ].map(f => (
                <div key={f.label} className="space-y-1">
                  <label className="text-xs text-muted-foreground uppercase tracking-wide">{f.label}</label>
                  <input
                    className="w-full bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-slate-600 focus:outline-none focus:border-primary/50 transition-colors"
                    placeholder={f.placeholder}
                    value={f.value}
                    onChange={e => f.onChange(e.target.value)}
                  />
                </div>
              ))}
              <div className="col-span-full">
                <p className="text-xs text-muted-foreground">
                  Metafields will be read and written as <span className="font-mono text-muted-foreground">{namespace}.{makeKey}</span>, <span className="font-mono text-muted-foreground">{namespace}.{modelKey}</span>, and <span className="font-mono text-muted-foreground">{namespace}.{yearKey}</span> on each product.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Custom Widget Code per Store */}
        {selectedStoreId && (
          <div className="bg-muted/30 border border-border/60 rounded-2xl overflow-hidden">
            <button
              onClick={() => setShowCustomCode(v => !v)}
              className="w-full flex items-center gap-2.5 px-5 py-4 hover:bg-muted/50 transition-colors text-left"
            >
              <FileCode className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="text-sm font-medium text-foreground flex-1">Custom Widget Code</span>
              {customCodeDirty && (
                <span className="flex items-center gap-1.5 text-[11px] text-amber-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                  Unsaved changes
                </span>
              )}
              {showCustomCode ? <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />}
            </button>

            {showCustomCode && (
              <div className="border-t border-border/60">
                <div className="flex items-start justify-between gap-3 px-5 py-3">
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Store-specific vehicle filter code (HTML / Liquid / JS). Saved per connected store — paste your quick-filter widget, custom section snippets, or any other code you need on this store.
                  </p>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(customCode);
                        toast({ title: 'Copied to clipboard' });
                      }}
                      disabled={!customCode}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-muted/50 border border-border text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Copy className="w-3 h-3" /> Copy
                    </button>
                    <button
                      onClick={saveCustomCode}
                      disabled={customCodeSaving || !customCodeDirty}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-500/20 border border-emerald-500/30 text-xs text-emerald-400 hover:bg-emerald-500/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {customCodeSaving
                        ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</>
                        : <><Save className="w-3 h-3" /> Save</>}
                    </button>
                  </div>
                </div>
                <div className="px-5 pb-5">
                  <textarea
                    className="w-full h-80 bg-black/50 border border-border rounded-xl px-4 py-3 text-xs text-foreground font-mono leading-relaxed placeholder:text-slate-700 outline-none focus:border-primary/40 transition-colors resize-y custom-scrollbar"
                    placeholder={"<!-- Paste your vehicle filter widget code here -->\n<!-- e.g. Quick Filter HTML, Liquid snippets, custom JS -->\n\n<div class=\"quick-filter\">\n  ...\n</div>"}
                    value={customCode}
                    onChange={e => {
                      setCustomCode(e.target.value);
                      setCustomCodeDirty(e.target.value !== savedCode);
                    }}
                    spellCheck={false}
                    autoComplete="off"
                  />
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[11px] text-slate-600 font-mono">
                      {customCode.split('\n').length} lines · {customCode.length.toLocaleString()} chars
                    </span>
                    {!customCodeDirty && savedCode && (
                      <span className="text-[11px] text-emerald-600 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Saved
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Widget Preview */}
        {selectedStoreId && (
          <div className="bg-muted/30 border border-border/60 rounded-2xl overflow-hidden">
            <button
              onClick={() => setShowPreview(v => !v)}
              className="w-full flex items-center gap-2.5 px-5 py-4 hover:bg-muted/50 transition-colors text-left"
            >
              <Monitor className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="text-sm font-medium text-foreground flex-1">Widget Editor</span>
              <span className="text-[11px] text-slate-600 mr-1">Live · mock data</span>
              {showPreview ? <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />}
            </button>

            {showPreview && (
              <div className="border-t border-border/60">
                {/* Header row */}
                <div className="flex items-center justify-between px-5 py-3">
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    A live render of the fitment filter widget with your current custom CSS applied. Uses mock vehicle data — no Shopify connection required.
                  </p>
                  <button
                    onClick={() => {
                      if (previewBaseJs) {
                        setPreviewSrcdoc(buildPreviewSrcdoc(previewBaseCss, customCode, previewBaseJs, widgetConfig));
                      } else {
                        loadPreviewAssets();
                      }
                    }}
                    disabled={previewLoading}
                    className="ml-4 shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-muted/50 border border-border text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {previewLoading
                      ? <><Loader2 className="w-3 h-3 animate-spin" /> Loading…</>
                      : <><RefreshCw className="w-3 h-3" /> Refresh Preview</>}
                  </button>
                </div>

                {/* ── Widget Fields Configuration ───────────────────────── */}
                <div className="mx-5 mb-4 rounded-xl border border-border/60 bg-muted/50 overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-2.5 border-b border-border/60">
                    <span className="text-xs font-medium text-foreground">Widget Fields</span>
                    <div className="flex items-center gap-2">
                      {widgetConfigDirty && (
                        <span className="flex items-center gap-1 text-[11px] text-amber-400">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                          Unsaved
                        </span>
                      )}
                      <button
                        onClick={saveWidgetConfig}
                        disabled={widgetConfigSaving || !widgetConfigDirty}
                        className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-500/20 border border-emerald-500/30 text-xs text-emerald-400 hover:bg-emerald-500/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {widgetConfigSaving
                          ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</>
                          : <><Save className="w-3 h-3" /> Save Settings</>}
                      </button>
                    </div>
                  </div>

                  {/* Field toggles */}
                  <div className="px-4 py-3">
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2.5">
                      {([
                        { key: 'showMake',        label: 'Make'         },
                        { key: 'showModel',       label: 'Model'        },
                        { key: 'showYear',        label: 'Year'         },
                        { key: 'showProductType', label: 'Product Type' },
                      ] as { key: keyof WidgetConfig; label: string }[]).map(({ key, label }) => {
                        const on = widgetConfig[key] as boolean;
                        return (
                          <div key={key} className="flex items-center justify-between gap-3 min-w-0">
                            <span className="text-xs text-muted-foreground truncate">{label}</span>
                            <button
                              type="button"
                              onClick={() => {
                                setWidgetConfig(c => ({ ...c, [key]: !on }));
                                setWidgetConfigDirty(true);
                              }}
                              className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${on ? 'bg-primary' : 'bg-muted'}`}
                              aria-label={`${on ? 'Hide' : 'Show'} ${label}`}
                            >
                              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform duration-200 ${on ? 'translate-x-4' : 'translate-x-0'}`} />
                            </button>
                          </div>
                        );
                      })}
                    </div>

                    {/* Page visibility */}
                    <div className="mt-3 pt-3 border-t border-border/60">
                      <p className="text-[11px] text-muted-foreground mb-2 font-medium uppercase tracking-wider">Page visibility</p>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-2.5">
                        {([
                          { key: 'showOnHome',       label: 'Homepage'         },
                          { key: 'showOnCollection', label: 'Collection pages' },
                          { key: 'showOnProduct',    label: 'Product pages'    },
                        ] as { key: keyof WidgetConfig; label: string }[]).map(({ key, label }) => {
                          const on = widgetConfig[key] as boolean;
                          return (
                            <div key={key} className="flex items-center justify-between gap-3 min-w-0">
                              <span className="text-xs text-muted-foreground truncate">{label}</span>
                              <button
                                type="button"
                                onClick={() => {
                                  setWidgetConfig(c => ({ ...c, [key]: !on }));
                                  setWidgetConfigDirty(true);
                                }}
                                className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${on ? 'bg-primary' : 'bg-muted'}`}
                                aria-label={`${on ? 'Hide' : 'Show'} filter on ${label}`}
                              >
                                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform duration-200 ${on ? 'translate-x-4' : 'translate-x-0'}`} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* ── Button Editor ─────────────────────────────────────── */}
                    <div className="mt-3 pt-3 border-t border-border/60">
                      <span className="text-xs text-muted-foreground block mb-2.5">Find Parts Button</span>
                      <div className="flex flex-col gap-2.5">
                        {/* Button text */}
                        <div className="flex items-center gap-2.5">
                          <Type className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                          <input
                            type="text"
                            value={widgetConfig.buttonText}
                            onChange={e => { setWidgetConfig(c => ({ ...c, buttonText: e.target.value })); setWidgetConfigDirty(true); }}
                            placeholder="Find Parts"
                            className="flex-1 bg-muted/50 border border-border rounded-lg px-2.5 py-1.5 text-xs text-foreground placeholder-slate-600 focus:outline-none focus:border-primary/50"
                          />
                        </div>
                        {/* Loading text */}
                        <div className="flex items-center gap-2.5">
                          <Loader2 className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                          <input
                            type="text"
                            value={widgetConfig.loadingText}
                            onChange={e => { setWidgetConfig(c => ({ ...c, loadingText: e.target.value })); setWidgetConfigDirty(true); }}
                            placeholder="Loading vehicle options…"
                            className="flex-1 bg-muted/50 border border-border rounded-lg px-2.5 py-1.5 text-xs text-foreground placeholder-slate-600 focus:outline-none focus:border-primary/50"
                          />
                        </div>
                        {/* Reset Vehicle text */}
                        <div className="flex items-center gap-2.5">
                          <RefreshCw className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                          <input
                            type="text"
                            value={widgetConfig.resetText}
                            onChange={e => { setWidgetConfig(c => ({ ...c, resetText: e.target.value })); setWidgetConfigDirty(true); }}
                            placeholder="Reset Vehicle"
                            className="flex-1 bg-muted/50 border border-border rounded-lg px-2.5 py-1.5 text-xs text-foreground placeholder-slate-600 focus:outline-none focus:border-primary/50"
                          />
                        </div>
                        {/* Button icon */}
                        <div className="flex items-start gap-2.5">
                          <Wand2 className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-1" />
                          <div className="flex flex-wrap gap-1.5">
                            {VFF_BUTTON_ICONS.map(icon => {
                              const active = (widgetConfig.buttonIcon || 'car') === icon.id;
                              return (
                                <button
                                  key={icon.id}
                                  type="button"
                                  title={icon.label}
                                  onClick={() => { setWidgetConfig(c => ({ ...c, buttonIcon: icon.id })); setWidgetConfigDirty(true); }}
                                  className={`w-9 h-9 flex items-center justify-center rounded-lg border transition-colors ${active ? 'border-primary bg-primary/20 text-primary' : 'border-border bg-muted/50 text-muted-foreground hover:border-border hover:text-foreground'}`}
                                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="none" stroke="currentColor" stroke-width="56" stroke-linecap="round" stroke-linejoin="round" style="width:16px;height:16px;pointer-events:none" aria-hidden="true">${icon.svg}</svg>`) }}
                                />
                              );
                            })}
                          </div>
                        </div>
                        {/* Button colors */}
                        {([
                          { key: 'buttonColor'    as keyof WidgetConfig, label: 'Find Parts bg',  title: 'Pick Find Parts button colour' },
                          { key: 'resetBgColor'   as keyof WidgetConfig, label: 'Reset bg',       title: 'Pick Reset button background colour' },
                          { key: 'resetTextColor' as keyof WidgetConfig, label: 'Reset text',     title: 'Pick Reset button text colour' },
                        ]).map(({ key, label, title }) => {
                          const val = widgetConfig[key] as string;
                          return (
                            <div key={key} className="flex items-center gap-2.5">
                              <Palette className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                              <span className="text-xs text-muted-foreground w-20 shrink-0">{label}</span>
                              <div className="flex items-center gap-2 flex-1">
                                <div className="relative shrink-0">
                                  <input
                                    type="color"
                                    value={val || '#000000'}
                                    onChange={e => { setWidgetConfig(c => ({ ...c, [key]: e.target.value })); setWidgetConfigDirty(true); }}
                                    className="w-7 h-7 rounded-md cursor-pointer border border-border bg-transparent p-0.5"
                                    title={title}
                                  />
                                </div>
                                <input
                                  type="text"
                                  value={val}
                                  onChange={e => { setWidgetConfig(c => ({ ...c, [key]: e.target.value })); setWidgetConfigDirty(true); }}
                                  placeholder="Theme default"
                                  className="flex-1 bg-muted/50 border border-border rounded-lg px-2.5 py-1.5 text-xs text-foreground placeholder-slate-600 focus:outline-none focus:border-primary/50 font-mono"
                                />
                                {val && (
                                  <button
                                    type="button"
                                    onClick={() => { setWidgetConfig(c => ({ ...c, [key]: '' })); setWidgetConfigDirty(true); }}
                                    className="text-slate-600 hover:text-foreground transition-colors"
                                    title="Reset to theme default"
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Product Type Values — only shown when Product Type is enabled */}
                    {widgetConfig.showProductType && (
                      <div className="mt-3 pt-3 border-t border-border/60">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-muted-foreground">
                            Product Type Values
                            {productTypes.length > 0 && (
                              widgetConfig.productTypeFilter.length > 0
                                ? <span className="ml-1.5 text-primary">({productTypes.length - widgetConfig.productTypeFilter.length}/{productTypes.length} shown)</span>
                                : <span className="ml-1.5 text-muted-foreground">(all {productTypes.length} shown)</span>
                            )}
                          </span>
                          <div className="flex items-center gap-1.5">
                            {productTypes.length > 0 && (
                              <>
                                <button
                                  onClick={() => { setWidgetConfig(c => ({ ...c, productTypeFilter: [] })); setWidgetConfigDirty(true); }}
                                  disabled={widgetConfig.productTypeFilter.length === 0}
                                  className="px-2 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-[11px] text-emerald-400 hover:bg-emerald-500/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                  Show All
                                </button>
                                <button
                                  onClick={() => { setWidgetConfig(c => ({ ...c, productTypeFilter: productTypes.slice() })); setWidgetConfigDirty(true); }}
                                  disabled={widgetConfig.productTypeFilter.length === productTypes.length}
                                  className="px-2 py-1 rounded-lg bg-muted/50 border border-border text-[11px] text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                  Hide All
                                </button>
                              </>
                            )}
                            <button
                              onClick={loadProductTypes}
                              disabled={productTypesLoading}
                              className="flex items-center gap-1 px-2 py-1 rounded-lg bg-muted/50 border border-border text-[11px] text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              {productTypesLoading
                                ? <><Loader2 className="w-3 h-3 animate-spin" /> Loading…</>
                                : <><RefreshCw className="w-3 h-3" /> {productTypes.length > 0 ? 'Reload' : 'Load from store'}</>}
                            </button>
                          </div>
                        </div>

                        {productTypes.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto custom-scrollbar">
                            {productTypes.map(type => {
                              // productTypeFilter is a DENY list — type is visible if NOT in the list
                              const visible = !widgetConfig.productTypeFilter.includes(type);
                              return (
                                <button
                                  key={type}
                                  type="button"
                                  onClick={() => {
                                    setWidgetConfig(c => {
                                      const filter = c.productTypeFilter;
                                      const next = filter.includes(type)
                                        ? filter.filter(t => t !== type)
                                        : [...filter, type];
                                      return { ...c, productTypeFilter: next };
                                    });
                                    setWidgetConfigDirty(true);
                                  }}
                                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs transition-colors ${
                                    visible
                                      ? 'bg-primary/15 border border-primary/30 text-primary'
                                      : 'bg-muted/50 border border-border text-muted-foreground line-through'
                                  }`}
                                >
                                  {visible
                                    ? <Eye className="w-3 h-3 shrink-0" />
                                    : <EyeOff className="w-3 h-3 shrink-0" />}
                                  {type}
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-[11px] text-slate-600 italic">
                            {productTypesLoading ? 'Loading product types from your store…' : 'Click "Load from store" to fetch your product types.'}
                          </p>
                        )}

                        {/* Default Product Type & hide dropdown */}
                        <div className="mt-3 pt-3 border-t border-border/60 flex flex-col gap-2.5">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <span className="text-xs text-muted-foreground block">Default selection</span>
                              <span className="text-[11px] text-slate-600">Pre-selects a type; customer can still change it</span>
                            </div>
                          </div>
                          <select
                            value={widgetConfig.defaultProductType}
                            onChange={e => { setWidgetConfig(c => ({ ...c, defaultProductType: e.target.value })); setWidgetConfigDirty(true); }}
                            className="w-full bg-muted/50 border border-border rounded-lg px-2.5 py-1.5 text-xs text-foreground focus:outline-none focus:border-primary/50"
                          >
                            <option value="">No default (show all)</option>
                            {productTypes
                              .filter(t => !widgetConfig.productTypeFilter.includes(t))
                              .map(t => <option key={t} value={t}>{t}</option>)}
                          </select>

                          {/* Hide dropdown toggle */}
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <span className="text-xs text-muted-foreground block">Hide dropdown</span>
                              <span className="text-[11px] text-slate-600">Silently filter by default type without showing the dropdown</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => { setWidgetConfig(c => ({ ...c, hideProductTypeDropdown: !c.hideProductTypeDropdown })); setWidgetConfigDirty(true); }}
                              className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${widgetConfig.hideProductTypeDropdown ? 'bg-primary' : 'bg-muted'}`}
                              aria-label={widgetConfig.hideProductTypeDropdown ? 'Show dropdown' : 'Hide dropdown'}
                            >
                              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform duration-200 ${widgetConfig.hideProductTypeDropdown ? 'translate-x-4' : 'translate-x-0'}`} />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ── Fitment Badge ──────────────────────────────────── */}
                    <div className="mt-3 pt-3 border-t border-border/60">
                      <span className="text-xs text-muted-foreground block mb-2.5">Fitment Badge</span>
                      <div className="flex flex-col gap-2.5">
                        {/* Allowed product types */}
                        <div>
                          <span className="text-[11px] text-muted-foreground block mb-1">Show on product types (blank = all)</span>
                          <input
                            type="text"
                            value={widgetConfig.badgeAllowedProductTypes}
                            onChange={e => { setWidgetConfig(c => ({ ...c, badgeAllowedProductTypes: e.target.value })); setWidgetConfigDirty(true); }}
                            placeholder="Wheels, Tyres"
                            className="w-full bg-muted/50 border border-border rounded-lg px-2.5 py-1.5 text-xs text-foreground placeholder-slate-600 focus:outline-none focus:border-primary/50"
                          />
                        </div>
                        {/* No vehicle label */}
                        <div className="flex items-center gap-2.5">
                          <Type className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                          <input
                            type="text"
                            value={widgetConfig.badgeNoVehicleText}
                            onChange={e => { setWidgetConfig(c => ({ ...c, badgeNoVehicleText: e.target.value })); setWidgetConfigDirty(true); }}
                            placeholder="Select your vehicle to check fitment"
                            className="flex-1 bg-muted/50 border border-border rounded-lg px-2.5 py-1.5 text-xs text-foreground placeholder-slate-600 focus:outline-none focus:border-primary/50"
                          />
                        </div>
                        {/* No-vehicle link override */}
                        <div>
                          <span className="text-[11px] text-muted-foreground block mb-1">No-vehicle link override (leave blank to anchor to selector)</span>
                          <input
                            type="text"
                            value={widgetConfig.badgeNoVehicleLink}
                            onChange={e => { setWidgetConfig(c => ({ ...c, badgeNoVehicleLink: e.target.value })); setWidgetConfigDirty(true); }}
                            placeholder="/collections/all"
                            className="w-full bg-muted/50 border border-border rounded-lg px-2.5 py-1.5 text-xs text-foreground placeholder-slate-600 focus:outline-none focus:border-primary/50"
                          />
                        </div>
                        {/* Badge icon picker */}
                        <div className="flex items-center gap-2.5">
                          <Wand2 className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                          <div className="flex gap-1.5">
                            {([
                              { id: 'car',   label: 'Car',         svg: '<path d="M80 224l37.78-88.15C123.93 121.5 139.6 112 157.11 112h197.78c17.51 0 33.18 9.5 39.33 23.85L432 224M80 224h352v144H80zM112 368v32H80v-32M432 368v32h-32v-32"/><circle cx="144" cy="288" r="16"/><circle cx="368" cy="288" r="16"/>' },
                              { id: 'truck', label: 'Truck / 4x4', svg: '<rect x="32" y="176" width="288" height="160" rx="16"/><path d="M320 208h80l48 96v32H320V208z"/><circle cx="112" cy="368" r="36"/><circle cx="368" cy="368" r="36"/>' },
                              { id: 'wheel', label: 'Wheel / Tyre', svg: '<circle cx="256" cy="256" r="220"/><circle cx="256" cy="256" r="72"/><line x1="256" y1="36" x2="256" y2="184"/><line x1="256" y1="328" x2="256" y2="476"/><line x1="36" y1="256" x2="184" y2="256"/><line x1="328" y1="256" x2="476" y2="256"/>' },
                            ] as { id: string; label: string; svg: string }[]).map(icon => {
                              const active = (widgetConfig.badgeIcon || 'car') === icon.id;
                              return (
                                <button
                                  key={icon.id}
                                  type="button"
                                  title={icon.label}
                                  onClick={() => { setWidgetConfig(c => ({ ...c, badgeIcon: icon.id })); setWidgetConfigDirty(true); }}
                                  className={`w-9 h-9 flex items-center justify-center rounded-lg border transition-colors ${active ? 'border-primary bg-primary/20 text-primary' : 'border-border bg-muted/50 text-muted-foreground hover:border-border hover:text-foreground'}`}
                                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="none" stroke="currentColor" stroke-width="56" stroke-linecap="round" stroke-linejoin="round" style="width:16px;height:16px;pointer-events:none" aria-hidden="true">${icon.svg}</svg>`) }}
                                />
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Live preview iframe */}
                <div className="px-5 pb-5">
                  {previewLoading && !previewSrcdoc ? (
                    <div className="flex items-center justify-center h-64 rounded-xl border border-border/60 bg-muted/50">
                      <Loader2 className="w-6 h-6 animate-spin text-slate-600" />
                    </div>
                  ) : (
                    <iframe
                      key={previewSrcdoc}
                      srcDoc={previewSrcdoc}
                      title="Widget Preview"
                      className="w-full rounded-xl border border-border/60 bg-black"
                      style={{ height: '340px', colorScheme: 'normal' }}
                      sandbox="allow-scripts"
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Stats bar */}
        {loaded && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Total Products',  value: stats.total,       color: 'text-foreground'       },
              { label: 'Catalogued',      value: stats.catalogued,  color: 'text-emerald-400' },
              { label: 'AI Generated',    value: stats.aiGenerated, color: 'text-primary'     },
              { label: 'Pending',         value: stats.pending,     color: 'text-muted-foreground'   },
            ].map(s => (
              <div key={s.label} className="bg-muted/30 border border-border/60 rounded-xl px-4 py-3">
                <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Toolbar */}
        {loaded && products.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {/* Catalogue status filter */}
            <div className="flex gap-1 p-1 bg-muted/50 border border-border/50 rounded-xl">
              {([
                { value: 'all',           label: 'All' },
                { value: 'uncatalogued',  label: 'Uncatalogued' },
                { value: 'catalogued',    label: 'Catalogued' },
                { value: 'supplier-data', label: 'Supplier Data' },
              ] as { value: StatusFilter; label: string }[]).map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => setStatusFilter(value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    statusFilter === value
                      ? 'bg-primary/20 border border-primary/30 text-primary'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Confidence filter — only shown when AI-generated products with scores exist */}
            {products.some(p => edits.get(p.id)?.aiGenerated && edits.get(p.id)?.confidence) && (
              <div className="flex items-center gap-1 p-1 bg-muted/50 border border-border/50 rounded-xl">
                {([
                  { value: 'all',    label: 'All',    dot: null },
                  { value: 'high',   label: 'High',   dot: 'bg-emerald-400' },
                  { value: 'medium', label: 'Medium', dot: 'bg-amber-400'   },
                  { value: 'low',    label: 'Low',    dot: 'bg-red-400'     },
                ] as const).map(({ value, label, dot }) => (
                  <button
                    key={value}
                    onClick={() => setConfidenceFilter(value)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      confidenceFilter === value
                        ? 'bg-primary/20 border border-primary/30 text-primary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    {dot && <span className={`inline-block w-2 h-2 rounded-full ${dot}`} />}
                    {label}
                  </button>
                ))}
              </div>
            )}

            {/* Shopify status filter */}
            <div className="flex gap-1 p-1 bg-muted/50 border border-border/50 rounded-xl">
              {(['all', 'ACTIVE', 'DRAFT'] as const).map(f => (
                <button
                  key={f}
                  onClick={() => setPublishFilter(f)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    publishFilter === f
                      ? 'bg-primary/20 border border-primary/30 text-primary'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  {f === 'all' ? 'All Status' : f.charAt(0) + f.slice(1).toLowerCase()}
                </button>
              ))}
            </div>

            {/* Vendor filter */}
            {uniqueVendors.length > 0 && (
              <Select value={vendorFilter || '__all__'} onValueChange={v => setVendorFilter(v === '__all__' ? '' : v)}>
                <SelectTrigger className="h-8 text-xs bg-muted/50 border-border text-foreground w-auto min-w-[120px] max-w-[180px]">
                  <SelectValue placeholder="Vendor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Vendors</SelectItem>
                  {uniqueVendors.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            )}

            {/* Exclude product types */}
            {uniqueTypes.length > 0 && (
              <div ref={excludeMenuRef} className="relative">
                <button
                  onClick={() => setShowExcludeMenu(v => !v)}
                  className={`flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-medium border transition-all ${
                    excludeTypes.size > 0
                      ? 'bg-red-500/10 border-red-500/30 text-red-400'
                      : 'bg-muted/50 border-border text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  <X className="w-3 h-3" />
                  Exclude Types
                  {excludeTypes.size > 0 && (
                    <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400 text-[10px] font-bold">
                      {excludeTypes.size}
                    </span>
                  )}
                  <ChevronDown className={`w-3 h-3 transition-transform ${showExcludeMenu ? 'rotate-180' : ''}`} />
                </button>

                {showExcludeMenu && (
                  <div className="absolute top-full left-0 mt-1 z-50 min-w-[200px] bg-card border border-border rounded-xl shadow-2xl overflow-hidden">
                    <div className="px-3 py-2 border-b border-border/60 flex items-center justify-between">
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Exclude from list</span>
                      {excludeTypes.size > 0 && (
                        <button
                          onClick={() => setExcludeTypes(new Set())}
                          className="text-[10px] text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                        >
                          Clear all
                        </button>
                      )}
                    </div>
                    <div className="max-h-52 overflow-y-auto py-1 custom-scrollbar">
                      {uniqueTypes.map(type => (
                        <label
                          key={type}
                          className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-muted/50 transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={excludeTypes.has(type)}
                            onChange={() => toggleExcludeType(type)}
                            className="accent-red-500 w-3.5 h-3.5 shrink-0"
                          />
                          <span className={`text-xs truncate ${excludeTypes.has(type) ? 'text-red-400 line-through opacity-60' : 'text-foreground'}`}>
                            {type || <em className="text-slate-600">No type</em>}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Search */}
            <div className="flex items-center gap-2 flex-1 min-w-[160px] max-w-xs px-3 py-2 bg-muted/50 border border-border rounded-xl focus-within:border-primary/50 transition-colors">
              <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <input
                className="flex-1 bg-transparent text-sm text-foreground placeholder:text-slate-600 outline-none"
                placeholder="Search products…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && (
                <button onClick={() => setSearch('')} className="text-muted-foreground hover:text-foreground">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <div className="flex-1" />

            {/* Quick select uncatalogued */}
            <button
              onClick={selectUncatalogued}
              className="px-3 py-1.5 rounded-lg bg-muted/50 border border-border text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              Select Uncatalogued
            </button>

            {/* Model Cleanup */}
            <button
              onClick={() => setShowModelCleanup(v => !v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                showModelCleanup
                  ? 'bg-violet-500/20 border-violet-500/30 text-violet-400'
                  : 'bg-muted/50 border-border text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <GitMerge className="w-3.5 h-3.5" />
              Model Cleanup
            </button>

            {/* Clear All Fitment */}
            {loaded && (
              <button
                onClick={() => { setClearConfirmText(''); setShowClearConfirm(true); }}
                disabled={clearingFitment || applying || generating}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-rose-500/25 bg-rose-500/10 text-xs font-medium text-rose-400 hover:bg-rose-500/20 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                title="Reset all make, model and year values on every product"
              >
                {clearingFitment ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                {clearingFitment ? 'Clearing…' : 'Clear Fitment'}
              </button>
            )}

            {/* Generate from ORI — only shown when user has offroad_imports access */}
            {oriAccessAllowed && (
              <button
                onClick={generateFromOri}
                disabled={generatingOri || generating || applying || selectedIds.size === 0}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-sky-500/15 hover:bg-sky-500/25 border border-sky-500/30 text-sky-400 text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                title="Use Off Road Imports supplier data to enrich AI fitment — products are matched by SKU"
              >
                {generatingOri ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {genOriProgress.phase === 'downloading_ori'
                      ? 'Fetching ORI…'
                      : genOriProgress.total > 0
                        ? `ORI ${genOriProgress.current}/${genOriProgress.total}`
                        : 'ORI…'}
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                    ORI Fitment{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
                  </>
                )}
              </button>
            )}

            {/* Generate */}
            <button
              onClick={() => { setYearToAdd(''); setShowAddYear(true); }}
              disabled={generating || generatingOri || applying || clearingFitment || selectedIds.size === 0}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-muted/50 hover:bg-muted border border-border text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              title="Add a year to selected products without replacing their existing years"
            >
              <Plus className="w-4 h-4" /> Add Year
            </button>

            <button
              onClick={generateDescriptions}
              disabled={generating || generatingOri || applying || selectedIds.size === 0}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/20 hover:bg-primary/30 border border-primary/30 text-primary text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {genProgress.total > 0 ? `${genProgress.current}/${genProgress.total}` : 'Generating…'}
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4" />
                  Generate AI{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
                </>
              )}
            </button>

            {/* Push all edits */}
            {edits.size > 0 && (
              <button
                onClick={pushAllEdits}
                disabled={applying || generating}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/30 text-amber-400 text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                title="Push all pending edits to Shopify without selecting them first"
              >
                {applying ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Pushing…</>
                ) : (
                  <><RefreshCw className="w-4 h-4" /> Push All Edits ({edits.size})</>
                )}
              </button>
            )}

            {/* Apply (selected) */}
            <button
              onClick={applyFitment}
              disabled={applying || generating || edits.size === 0}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              title="Apply only selected products to Shopify"
            >
              {applying ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Applying…</>
              ) : (
                <><Check className="w-4 h-4" /> Apply Selected{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}</>
              )}
            </button>
          </div>
        )}

        {/* Paste banner */}
        <Dialog open={showAddYear} onOpenChange={setShowAddYear}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add Year to selected products</DialogTitle>
              <DialogDescription>
                Existing years, make and model are preserved. This creates pending edits only; use Apply Selected afterwards to save to Shopify.
              </DialogDescription>
            </DialogHeader>
            <label htmlFor="bulk-fitment-year" className="text-sm font-medium">Year to add</label>
            <input
              id="bulk-fitment-year"
              inputMode="numeric"
              maxLength={4}
              value={yearToAdd}
              onChange={event => setYearToAdd(event.target.value)}
              placeholder="2027"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              aria-describedby="bulk-fitment-year-preview"
            />
            <div id="bulk-fitment-year-preview" className="text-sm text-muted-foreground" aria-live="polite">
              {addYearPreview?.error ? addYearPreview.error : addYearPreview ? (
                <>{addYearPreview.changes.size} products will be updated; {addYearPreview.skipped} already include {yearToAdd.trim()} and will be skipped.</>
              ) : yearToAdd ? 'Enter a four-digit year, for example 2027.' : `${selectedIds.size} products selected, including selections on other pages.`}
            </div>
            <DialogFooter>
              <button onClick={() => setShowAddYear(false)} className="rounded-lg border border-border px-3 py-2 text-sm">Cancel</button>
              <button
                onClick={addYearToSelected}
                disabled={!addYearPreview || !!addYearPreview.error || addYearPreview.changes.size === 0 || applying || generating || generatingOri || clearingFitment}
                className="rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Add Year to {addYearPreview?.changes.size ?? 0} products
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {(copied.make || copied.model || copied.year) && selectedIds.size > 0 && (
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-violet-500/10 border border-violet-500/20 text-sm flex-wrap">
            <ClipboardPaste className="w-3.5 h-3.5 text-violet-400 shrink-0" />
            <span className="text-violet-300 font-medium">Paste to {selectedIds.size} selected:</span>
            {copied.make && (
              <button
                onClick={() => pasteToSelected('make')}
                className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-violet-500/20 hover:bg-violet-500/35 border border-violet-500/30 text-violet-300 hover:text-foreground transition-colors"
              >
                <span className="text-violet-500 text-xs">Make</span>
                <span className="text-foreground text-xs font-medium">{parseTagArray(copied.make).join(', ')}</span>
              </button>
            )}
            {copied.model && (
              <button
                onClick={() => pasteToSelected('model')}
                className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-violet-500/20 hover:bg-violet-500/35 border border-violet-500/30 text-violet-300 hover:text-foreground transition-colors"
              >
                <span className="text-violet-500 text-xs">Model</span>
                <span className="text-foreground text-xs font-medium">{parseTagArray(copied.model).join(', ')}</span>
              </button>
            )}
            {copied.year && (
              <button
                onClick={() => pasteToSelected('year')}
                className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-violet-500/20 hover:bg-violet-500/35 border border-violet-500/30 text-violet-300 hover:text-foreground transition-colors"
              >
                <span className="text-violet-500 text-xs">Year</span>
                <span className="text-foreground text-xs font-medium">{parseTagArray(copied.year).join(', ')}</span>
              </button>
            )}
            <button
              onClick={() => setCopied({ make: '', model: '', year: '' })}
              className="ml-auto text-violet-500 hover:text-foreground transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Gen error banner */}
        {genError && (
          <div className="flex items-center gap-2 px-4 py-3 rounded-xl border bg-red-500/10 border-red-500/20 text-red-400 text-sm">
            <XCircle className="w-4 h-4 shrink-0" />
            <span className="flex-1">{genError}</span>
            <button onClick={() => setGenError(null)} className="text-red-500 hover:text-foreground transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Apply result banner */}
        {applyResult && (
          <div className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-sm ${
            applyResult.ok
              ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
              : 'bg-red-500/10 border-red-500/20 text-red-400'
          }`}>
            {applyResult.ok ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <XCircle className="w-4 h-4 shrink-0" />}
            {applyResult.message}
          </div>
        )}

        {/* Product table */}
        {loaded && products.length === 0 && (
          <div className="text-center py-16 text-muted-foreground">
            <Car className="w-8 h-8 mx-auto mb-3 opacity-30" />
            <p>No products found in this store.</p>
          </div>
        )}

        {filteredProducts.length > 0 && (
          <div className="bg-muted/30 border border-border/60 rounded-2xl overflow-hidden">
            {/* Table header */}
            <div className="grid grid-cols-[32px_2fr_130px_1fr_110px_110px] gap-3 px-4 py-2.5 bg-muted/30 border-b border-border/50 text-xs text-muted-foreground uppercase tracking-wide items-center">
              <input
                type="checkbox"
                checked={allVisibleSelected}
                onChange={toggleAll}
                className="accent-primary"
              />
              <span>Product</span>
              <span>Make</span>
              <span>Model</span>
              <span>Year</span>
              <span>Status</span>
            </div>

            {/* Select-all-filtered banner */}
            {(someNotOnPageSelected || allFilteredSelected) && (
              <div className="flex items-center justify-center gap-2 px-4 py-2 bg-primary/5 border-b border-primary/20 text-xs text-muted-foreground">
                {allFilteredSelected ? (
                  <>
                    <span>All <span className="font-semibold text-foreground">{filteredProducts.length}</span> products are selected.</span>
                    <button onClick={() => setSelectedIds(new Set())} className="text-primary hover:underline font-medium">Clear selection</button>
                  </>
                ) : (
                  <>
                    <span>All <span className="font-semibold text-foreground">{paginatedProducts.length}</span> products on this page are selected.</span>
                    <button onClick={selectAllFiltered} className="text-primary hover:underline font-medium">
                      Select all {filteredProducts.length} products
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Table rows */}
            <div className="divide-y divide-white/5">
              {paginatedProducts.map(product => {
                const status = getStatus(product, edits);
                const edit   = edits.get(product.id);
                const isCat  = status === 'catalogued';

                const displayMake  = edit?.make  ?? product.make;
                const displayModel = edit?.model ?? product.model;
                const displayYear  = edit?.year  ?? product.year;
                const isUniversal  = parseTagArray(displayMake).length === 1 && parseTagArray(displayMake)[0] === 'Universal';

                return (
                  <div
                    key={product.id}
                    onClick={() => toggleSelect(product.id)}
                    className={`group grid grid-cols-[32px_2fr_130px_1fr_110px_110px] gap-3 px-4 py-3 items-start transition-colors cursor-pointer ${
                      selectedIds.has(product.id) ? 'bg-primary/5' : 'hover:bg-muted/50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(product.id)}
                      onChange={() => toggleSelect(product.id)}
                      onClick={e => e.stopPropagation()}
                      className="accent-primary mt-1"
                    />

                    <div className="min-w-0 pt-0.5">
                      <p className="text-sm text-foreground truncate" title={product.title}>{product.title}</p>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                        <span className="text-xs text-slate-600 truncate">{product.handle}</span>
                        {product.vendor && (
                          <span className="text-xs text-muted-foreground truncate">{product.vendor}</span>
                        )}
                        {product.productType && (
                          <span className="text-xs text-muted-foreground truncate">{product.productType}</span>
                        )}
                        <span className={`text-[10px] font-semibold uppercase tracking-wide ${
                          product.publishedStatus === 'ACTIVE' ? 'text-emerald-600' : 'text-amber-600'
                        }`}>
                          {product.publishedStatus}
                        </span>
                      </div>
                    </div>

                    {/* Make */}
                    <div onClick={e => e.stopPropagation()} className="relative">
                      {(isCat && !editingIds.has(product.id)) ? (
                        <button
                          onClick={() => startEditing(product)}
                          className="group/cell flex flex-wrap gap-1 pt-0.5 text-left w-full"
                          title="Click to edit"
                        >
                          {isUniversal ? (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-500/15 border border-amber-500/30 rounded text-xs text-amber-400">
                              <Globe className="w-3 h-3" /> Universal
                            </span>
                          ) : (
                            <>
                              {parseTagArray(displayMake).map((t, i) => (
                                <span key={i} className="px-1.5 py-0.5 bg-muted/50 border border-border rounded text-xs text-foreground group-hover/cell:border-violet-500/40 transition-colors">{t}</span>
                              ))}
                              {!displayMake && (
                                <span className="flex items-center gap-1 text-xs text-slate-600 group-hover/cell:text-violet-400 transition-colors">
                                  <Pencil className="w-3 h-3" /> Add make
                                </span>
                              )}
                              {displayMake && <Pencil className="w-3 h-3 text-slate-600 opacity-0 group-hover/cell:opacity-100 self-center ml-0.5 transition-opacity" />}
                            </>
                          )}
                        </button>
                      ) : (
                        <div className="flex flex-col gap-1.5">
                          {/* Universal fit toggle */}
                          <button
                            type="button"
                            onClick={() => setUniversalFit(product, !isUniversal)}
                            className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border transition-colors ${
                              isUniversal
                                ? 'bg-amber-500/20 border-amber-500/40 text-amber-400 hover:bg-amber-500/10'
                                : 'bg-muted/50 border-border text-muted-foreground hover:text-foreground hover:border-primary/40'
                            }`}
                          >
                            <Globe className="w-3 h-3" />
                            Universal fit
                          </button>
                          {/* Make picker — hidden when Universal */}
                          {!isUniversal && (
                            <FitmentTagPicker
                              value={displayMake}
                              onChange={v => updateEdit(product, 'make', v)}
                              options={allMakes}
                              placeholder="Select make…"
                            />
                          )}
                        </div>
                      )}
                      {displayMake && !isUniversal && (
                        <button
                          onClick={e => { e.stopPropagation(); copyField(product.id, 'make', displayMake); }}
                          className="absolute -top-0.5 right-0 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded text-muted-foreground hover:text-violet-400"
                          title="Copy make"
                        >
                          {copyFlash?.id === product.id && copyFlash.field === 'make'
                            ? <Check className="w-3 h-3 text-emerald-400" />
                            : <Copy className="w-3 h-3" />}
                        </button>
                      )}
                    </div>

                    {/* Model */}
                    <div onClick={e => e.stopPropagation()} className="relative">
                      {(isCat && !editingIds.has(product.id)) ? (
                        <button
                          onClick={() => startEditing(product)}
                          className="group/cell flex flex-wrap gap-1 pt-0.5 text-left w-full"
                          title="Click to edit"
                        >
                          {parseTagArray(displayModel).map((t, i) => (
                            <span key={i} className="px-1.5 py-0.5 bg-muted/50 border border-border rounded text-xs text-foreground group-hover/cell:border-violet-500/40 transition-colors">{t}</span>
                          ))}
                          {!displayModel && !isUniversal && (
                            <span className="flex items-center gap-1 text-xs text-slate-600 group-hover/cell:text-violet-400 transition-colors">
                              <Pencil className="w-3 h-3" /> Add model
                            </span>
                          )}
                          {isUniversal && !displayModel && (
                            <span className="text-xs text-amber-500/60 italic">Fits all</span>
                          )}
                          {displayModel && <Pencil className="w-3 h-3 text-slate-600 opacity-0 group-hover/cell:opacity-100 self-center ml-0.5 transition-opacity" />}
                        </button>
                      ) : isUniversal ? (
                        <p className="text-xs text-amber-500/50 italic pt-0.5">Fits all vehicles</p>
                      ) : (
                        <FitmentTagPicker
                          value={displayModel}
                          onChange={v => updateEdit(product, 'model', v)}
                          options={(() => {
                            const selectedMakes = parseTagArray(displayMake);
                            const src = selectedMakes.length > 0 ? selectedMakes : allMakes;
                            return [...new Set(
                              src.flatMap(mk => [...(makeModelTree.get(mk)?.keys() ?? [])]).filter(Boolean)
                            )].sort((a, b) => a.localeCompare(b));
                          })()}
                          placeholder="Select model…"
                        />
                      )}
                      {displayModel && !isUniversal && (
                        <button
                          onClick={e => { e.stopPropagation(); copyField(product.id, 'model', displayModel); }}
                          className="absolute -top-0.5 right-0 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded text-muted-foreground hover:text-violet-400"
                          title="Copy model"
                        >
                          {copyFlash?.id === product.id && copyFlash.field === 'model'
                            ? <Check className="w-3 h-3 text-emerald-400" />
                            : <Copy className="w-3 h-3" />}
                        </button>
                      )}
                    </div>

                    {/* Year */}
                    <div onClick={e => e.stopPropagation()} className="relative">
                      {(isCat && !editingIds.has(product.id)) ? (
                        <button
                          onClick={() => startEditing(product)}
                          className="group/cell flex flex-wrap gap-1 pt-0.5 text-left w-full"
                          title="Click to edit"
                        >
                          {parseTagArray(displayYear).map((t, i) => (
                            <span key={i} className="px-1.5 py-0.5 bg-muted/50 border border-border rounded text-xs text-foreground group-hover/cell:border-violet-500/40 transition-colors">{t}</span>
                          ))}
                          {!displayYear && !isUniversal && (
                            <span className="flex items-center gap-1 text-xs text-slate-600 group-hover/cell:text-violet-400 transition-colors">
                              <Pencil className="w-3 h-3" /> Add year
                            </span>
                          )}
                          {isUniversal && !displayYear && (
                            <span className="text-xs text-amber-500/60 italic">Fits all</span>
                          )}
                          {displayYear && <Pencil className="w-3 h-3 text-slate-600 opacity-0 group-hover/cell:opacity-100 self-center ml-0.5 transition-opacity" />}
                        </button>
                      ) : isUniversal ? (
                        <p className="text-xs text-amber-500/50 italic pt-0.5">Fits all vehicles</p>
                      ) : (
                        <FitmentTagPicker
                          value={displayYear}
                          onChange={v => updateEdit(product, 'year', v)}
                          options={allYears}
                          placeholder="Select year…"
                        />
                      )}
                      {displayYear && !isUniversal && (
                        <button
                          onClick={e => { e.stopPropagation(); copyField(product.id, 'year', displayYear); }}
                          className="absolute -top-0.5 right-0 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded text-muted-foreground hover:text-violet-400"
                          title="Copy year"
                        >
                          {copyFlash?.id === product.id && copyFlash.field === 'year'
                            ? <Check className="w-3 h-3 text-emerald-400" />
                            : <Copy className="w-3 h-3" />}
                        </button>
                      )}
                    </div>

                    <div className="pt-0.5 flex items-center gap-1.5 flex-wrap">
                      <StatusBadge status={status} />
                      {status === 'ai' && <ConfidencePill level={edits.get(product.id)?.confidence} />}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Table footer — pagination */}
            <div className="px-4 py-2.5 bg-muted/30 border-t border-border/50 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              {/* Count */}
              <span>
                {filteredProducts.length === 0 ? 'No products' : (
                  <>
                    {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, filteredProducts.length)}
                    {' '}of{' '}
                    <span className="text-foreground">{filteredProducts.length}</span>
                    {filteredProducts.length !== products.length && (
                      <span className="text-slate-600"> (filtered from {products.length})</span>
                    )}
                  </>
                )}
              </span>

              {selectedIds.size > 0 && (
                <span className="text-primary">{selectedIds.size} selected</span>
              )}

              <div className="flex-1" />

              {/* Page size */}
              <div className="flex items-center gap-1.5">
                <span className="text-slate-600">Per page:</span>
                {[25, 50, 100].map(s => (
                  <button
                    key={s}
                    onClick={() => { setPageSize(s); setPage(1); }}
                    className={`px-2 py-0.5 rounded text-xs transition-colors ${
                      pageSize === s
                        ? 'bg-primary/20 text-primary border border-primary/30'
                        : 'hover:bg-muted/50 text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>

              {/* Prev / page numbers / Next */}
              {totalPages > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    className="px-2 py-0.5 rounded hover:bg-muted/50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    ‹
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(n => n === 1 || n === totalPages || Math.abs(n - safePage) <= 1)
                    .reduce<(number | '…')[]>((acc, n, idx, arr) => {
                      if (idx > 0 && n - (arr[idx - 1] as number) > 1) acc.push('…');
                      acc.push(n);
                      return acc;
                    }, [])
                    .map((item, i) =>
                      item === '…' ? (
                        <span key={`ellipsis-${i}`} className="px-1 text-slate-600">…</span>
                      ) : (
                        <button
                          key={item}
                          onClick={() => setPage(item as number)}
                          className={`min-w-[24px] px-1.5 py-0.5 rounded text-xs transition-colors ${
                            safePage === item
                              ? 'bg-primary/20 text-primary border border-primary/30'
                              : 'hover:bg-muted/50 text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          {item}
                        </button>
                      )
                    )
                  }

                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={safePage === totalPages}
                    className="px-2 py-0.5 rounded hover:bg-muted/50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    ›
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        </>)}

      </div>

      {/* ── Model Cleanup Panel ─────────────────────────────────────────── */}
      {showModelCleanup && (
        <>
          <div
            className="fixed inset-0 z-40 bg-muted/50"
            onClick={() => setShowModelCleanup(false)}
          />
          <div className="fixed right-0 top-0 bottom-0 z-50 w-[400px] bg-card border-l border-border flex flex-col shadow-2xl">

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/60 shrink-0">
              <div className="flex items-center gap-2">
                <GitMerge className="w-4 h-4 text-violet-400" />
                <h3 className="text-sm font-semibold text-foreground">Model Cleanup</h3>
                {selectedModelKeys.size > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full bg-violet-500/20 text-violet-400 text-[10px] font-bold">
                    {selectedModelKeys.size} selected
                  </span>
                )}
              </div>
              <button onClick={() => setShowModelCleanup(false)} className="text-muted-foreground hover:text-foreground transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Hint */}
            <p className="px-4 py-2.5 text-[11px] text-muted-foreground border-b border-border/50 shrink-0">
              Browse makes and models. Select incorrectly named entries, type the correct name, then Apply.
            </p>

            {/* Year filter toggle */}
            <div className="flex items-center gap-1 px-4 py-2 border-b border-border/50 shrink-0">
              {(['all', 'has-gaps', 'no-years'] as const).map(f => (
                <button
                  key={f}
                  onClick={() => setCleanupYearFilter(f)}
                  className={`px-2.5 py-1 rounded text-[11px] font-medium transition-colors ${
                    cleanupYearFilter === f
                      ? f === 'has-gaps'
                        ? 'bg-amber-500/20 text-amber-400'
                        : f === 'no-years'
                          ? 'bg-rose-500/20 text-rose-400'
                          : 'bg-muted text-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  {f === 'all' ? 'All' : f === 'has-gaps' ? 'Has gaps' : 'No years'}
                </button>
              ))}
            </div>

            {/* Tree */}
            <div className="flex-1 overflow-y-auto py-1 custom-scrollbar">
              {filteredMakeModelTree.size === 0 && (
                <p className="text-center text-slate-600 text-xs py-8">
                  {cleanupYearFilter === 'all' ? 'No fitment data loaded yet.' : 'No models match this filter.'}
                </p>
              )}
              {[...filteredMakeModelTree.entries()].map(([make, modelMap]) => {
                const totalProds = [...modelMap.values()].reduce((s, ids) => s + ids.length, 0);
                const isExpanded = expandedMakes.has(make);
                const selCount = [...modelMap.keys()].filter(m => selectedModelKeys.has(`${make}::${m}`)).length;
                const isMakeHidden = hiddenMakes.has(make);

                return (
                  <div key={make}>
                    {/* Make row */}
                    <div className="flex items-center">
                      {(cleanupMode === 'remove' || cleanupMode === 'assign') && (
                        <input
                          type="checkbox"
                          checked={selectedMakeKeys.has(make)}
                          onChange={() => setSelectedMakeKeys(prev => {
                            const next = new Set(prev);
                            if (next.has(make)) next.delete(make); else next.add(make);
                            return next;
                          })}
                          className={`ml-3.5 w-3.5 h-3.5 shrink-0 ${cleanupMode === 'assign' ? 'accent-sky-500' : 'accent-red-500'}`}
                          title={cleanupMode === 'assign' ? 'Assign make to all products in this group' : 'Remove this make from all matching products'}
                        />
                      )}
                      {cleanupMode === 'hide' && (
                        <button
                          type="button"
                          onClick={() => toggleHiddenMake(make)}
                          className="ml-3 shrink-0 p-0.5 rounded transition-colors hover:bg-muted"
                          title={isMakeHidden ? 'Show this make in widget' : 'Hide this make from widget'}
                        >
                          {isMakeHidden
                            ? <EyeOff className="w-3.5 h-3.5 text-amber-500/70" />
                            : <Eye    className="w-3.5 h-3.5 text-muted-foreground" />}
                        </button>
                      )}
                      <button
                        onClick={() => setExpandedMakes(prev => {
                          const next = new Set(prev);
                          if (next.has(make)) next.delete(make); else next.add(make);
                          return next;
                        })}
                        className="flex-1 flex items-center gap-2 px-4 py-2.5 hover:bg-muted/50 transition-colors text-left"
                      >
                        {isExpanded
                          ? <ChevronDown  className="w-3 h-3 text-muted-foreground shrink-0" />
                          : <ChevronRight className="w-3 h-3 text-muted-foreground shrink-0" />}
                        <span className={`text-sm font-semibold flex-1 truncate ${
                          isMakeHidden && cleanupMode === 'hide'
                            ? 'text-slate-600 line-through'
                            : selectedMakeKeys.has(make)
                              ? cleanupMode === 'assign' ? 'text-sky-400' : 'text-red-400 line-through'
                              : 'text-foreground'
                        }`}>{make}</span>
                        {selCount > 0 && (
                          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold shrink-0 ${
                            cleanupMode === 'remove' ? 'bg-red-500/20 text-red-400'
                            : cleanupMode === 'assign' ? 'bg-sky-500/20 text-sky-400'
                            : 'bg-violet-500/20 text-violet-400'
                          }`}>
                            {selCount}
                          </span>
                        )}
                        <span className="text-[11px] text-slate-600 shrink-0">{totalProds}p</span>
                      </button>
                    </div>

                    {/* Model rows */}
                    {isExpanded && (
                      <div className="pb-1">
                        {[...modelMap.entries()].map(([model, productIds]) => {
                          const key = `${make}::${model}`;
                          const checked = selectedModelKeys.has(key);
                          const isHidden = hiddenModels.has(key);
                          const modelYears = makeModelYears.get(make)?.get(model) ?? [];
                          const yearsExpanded = expandedModelYears.has(key);

                          const toggleYears = (e: React.MouseEvent) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setExpandedModelYears(prev => {
                              const next = new Set(prev);
                              if (next.has(key)) next.delete(key); else next.add(key);
                              return next;
                            });
                          };

                          // Inline year coverage panel rendered below each model row
                          const yearPanel = yearsExpanded && modelYears.length > 0 && (
                            <div className="pl-12 pr-4 pb-2 pt-0.5">
                              <div className="flex flex-wrap gap-1 items-center">
                                {modelYears.map((y, i) => {
                                  const prev = modelYears[i - 1];
                                  const hasGap = prev !== undefined && y - prev > 1;
                                  return (
                                    <React.Fragment key={y}>
                                      {hasGap && (
                                        <span
                                          className="text-[10px] text-amber-500/60 px-0.5 select-none"
                                          title={`Gap: ${prev + 1}–${y - 1} missing`}
                                        >
                                          ···
                                        </span>
                                      )}
                                      <span className="px-1.5 py-0.5 rounded bg-muted/40 border border-border/60 text-[10px] text-muted-foreground tabular-nums leading-none">
                                        {y}
                                      </span>
                                    </React.Fragment>
                                  );
                                })}
                              </div>
                            </div>
                          );

                          if (cleanupMode === 'hide') {
                            return (
                              <React.Fragment key={key}>
                                <div
                                  onClick={() => toggleHiddenModel(make, model)}
                                  className="flex items-center gap-2.5 pl-9 pr-4 py-1.5 cursor-pointer hover:bg-muted/50 transition-colors select-none"
                                >
                                  {isHidden
                                    ? <EyeOff className="w-3.5 h-3.5 shrink-0 text-amber-500/70" />
                                    : <Eye className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
                                  }
                                  <span className={`text-xs flex-1 truncate ${isHidden ? 'text-slate-600 line-through' : model ? 'text-foreground' : 'text-slate-600 italic'}`}>
                                    {model || 'No model set'}
                                  </span>
                                  <span className="text-[10px] text-slate-600 shrink-0 tabular-nums">
                                    {productIds.length}
                                  </span>
                                  {modelYears.length > 0 && (
                                    <button
                                      onClick={toggleYears}
                                      className="ml-0.5 p-0.5 rounded text-slate-600 hover:text-muted-foreground transition-colors shrink-0"
                                      title={yearsExpanded ? 'Hide year coverage' : `Show year coverage (${modelYears[0]}–${modelYears[modelYears.length - 1]})`}
                                    >
                                      {yearsExpanded
                                        ? <ChevronUp   className="w-3 h-3" />
                                        : <ChevronDown className="w-3 h-3" />}
                                    </button>
                                  )}
                                </div>
                                {yearPanel}
                              </React.Fragment>
                            );
                          }

                          return (
                            <React.Fragment key={key}>
                              <label
                                className={`flex items-center gap-2.5 pl-9 pr-4 py-1.5 cursor-pointer transition-colors ${
                                  checked ? 'bg-violet-500/8 hover:bg-violet-500/12' : 'hover:bg-muted/50'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => {
                                    setSelectedModelKeys(prev => {
                                      const next = new Set(prev);
                                      if (next.has(key)) next.delete(key); else next.add(key);
                                      if (cleanupMode !== 'remove') {
                                        if (next.size === 1 && next.has(key)) {
                                          setRenameValue(model);
                                        } else if (next.size === 0) {
                                          setRenameValue('');
                                        }
                                      }
                                      return next;
                                    });
                                  }}
                                  className={`w-3.5 h-3.5 shrink-0 ${cleanupMode === 'remove' ? 'accent-red-500' : cleanupMode === 'assign' ? 'accent-sky-500' : 'accent-violet-500'}`}
                                />
                                <span className={`text-xs flex-1 truncate ${checked ? (cleanupMode === 'remove' ? 'text-red-400 line-through' : cleanupMode === 'assign' ? 'text-sky-300' : 'text-violet-300') : model ? 'text-foreground' : 'text-slate-600 italic'}`}>
                                  {model || 'No model set'}
                                </span>
                                <span className="text-[10px] text-slate-600 shrink-0 tabular-nums">
                                  {productIds.length}
                                </span>
                                {modelYears.length > 0 && (
                                  <button
                                    onClick={toggleYears}
                                    className="ml-0.5 p-0.5 rounded text-slate-600 hover:text-muted-foreground transition-colors shrink-0"
                                    title={yearsExpanded ? 'Hide year coverage' : `Show year coverage (${modelYears[0]}–${modelYears[modelYears.length - 1]})`}
                                  >
                                    {yearsExpanded
                                      ? <ChevronUp   className="w-3 h-3" />
                                      : <ChevronDown className="w-3 h-3" />}
                                  </button>
                                )}
                              </label>
                              {yearPanel}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Footer — rename / split / remove actions */}
            <div className="border-t border-border/60 px-4 pt-3 pb-4 space-y-3 shrink-0">

              {/* Selection count + clear */}
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">
                  {selectedModelKeys.size === 0 && selectedMakeKeys.size === 0
                    ? 'Select makes or models above'
                    : [
                        selectedMakeKeys.size > 0 && `${selectedMakeKeys.size} make${selectedMakeKeys.size !== 1 ? 's' : ''}`,
                        selectedModelKeys.size > 0 && `${selectedModelKeys.size} model${selectedModelKeys.size !== 1 ? 's' : ''}`,
                      ].filter(Boolean).join(' + ') + ' selected'}
                </span>
                {(selectedModelKeys.size > 0 || selectedMakeKeys.size > 0) && (
                  <button
                    onClick={() => { setSelectedModelKeys(new Set()); setSelectedMakeKeys(new Set()); setRenameValue(''); setSplitValue(''); }}
                    className="text-slate-600 hover:text-foreground transition-colors text-[11px]"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Mode tabs */}
              <div className="grid grid-cols-5 rounded-lg overflow-hidden border border-border/60 text-[11px] font-medium">
                {([
                  { id: 'rename', label: 'Rename' },
                  { id: 'split',  label: 'Split' },
                  { id: 'remove', label: 'Remove' },
                  { id: 'assign', label: 'Assign' },
                  { id: 'hide',   label: 'Hide' },
                ] as const).map(({ id, label }) => (
                  <button
                    key={id}
                    onClick={() => setCleanupMode(id)}
                    className={`py-1.5 transition-colors leading-tight px-1 ${
                      cleanupMode === id
                        ? id === 'remove' ? 'bg-red-500/20 text-red-700 dark:text-red-300'
                          : id === 'assign' ? 'bg-sky-500/20 text-sky-300'
                          : id === 'hide'   ? 'bg-amber-500/20 text-amber-300'
                          : 'bg-violet-500/20 text-violet-300'
                        : 'bg-muted/30 text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* ── Rename mode ── */}
              {cleanupMode === 'rename' && (
                <>
                  <div className="flex gap-2">
                    <input
                      className="flex-1 bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-slate-600 outline-none focus:border-violet-500/50 transition-colors"
                      placeholder="Rename selected to…"
                      value={renameValue}
                      onChange={e => setRenameValue(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') applyModelRename(); }}
                    />
                    <button
                      onClick={applyModelRename}
                      disabled={!renameValue.trim() || selectedModelKeys.size === 0}
                      className="px-3 py-2 rounded-lg bg-violet-500/20 hover:bg-violet-500/30 border border-violet-500/30 text-violet-400 text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      Apply
                    </button>
                  </div>
                  {selectedModelKeys.size > 0 && renameValue.trim() && (
                    <p className="text-[11px] text-slate-600 leading-relaxed">
                      Replaces {selectedModelKeys.size} name{selectedModelKeys.size !== 1 ? 's' : ''} →{' '}
                      <span className="text-violet-400">"{renameValue.trim()}"</span> across all matching products.
                    </p>
                  )}
                </>
              )}

              {/* ── Split mode ── */}
              {cleanupMode === 'split' && (
                <>
                  <div className="relative">
                    <textarea
                      rows={4}
                      className="w-full bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-slate-600 outline-none focus:border-violet-500/50 transition-colors resize-none font-mono leading-relaxed"
                      placeholder={"One model per line, e.g.:\nFord Ranger PX1\nFord Ranger PX2\nFord Ranger PX3"}
                      value={splitValue}
                      onChange={e => setSplitValue(e.target.value)}
                    />
                  </div>
                  {(() => {
                    const lines = splitValue.split('\n').map(s => s.trim()).filter(Boolean);
                    return (
                      <>
                        <button
                          onClick={applyModelSplit}
                          disabled={lines.length < 2 || selectedModelKeys.size === 0}
                          className="w-full px-3 py-2 rounded-lg bg-violet-500/20 hover:bg-violet-500/30 border border-violet-500/30 text-violet-400 text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Split into {lines.length > 1 ? lines.length : '…'} values
                        </button>
                        {selectedModelKeys.size > 0 && lines.length >= 2 && (
                          <p className="text-[11px] text-slate-600 leading-relaxed">
                            Each selected model name will be replaced by these {lines.length} values across all matching products.
                          </p>
                        )}
                        {lines.length === 1 && (
                          <p className="text-[11px] text-amber-600">Enter at least 2 lines to split.</p>
                        )}
                      </>
                    );
                  })()}
                </>
              )}

              {/* ── Remove mode ── */}
              {cleanupMode === 'remove' && (
                <>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    Check the <span className="text-red-400">make</span> checkbox to remove an entire make tag, or check individual model names to remove just those model tags. Changes are saved directly to Shopify.
                  </p>
                  <button
                    onClick={removeAndPush}
                    disabled={(selectedModelKeys.size === 0 && selectedMakeKeys.size === 0) || applying}
                    className="w-full px-3 py-2 rounded-lg bg-red-500/15 hover:bg-red-500/25 border border-red-500/25 text-red-400 text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {applying
                      ? 'Saving to Shopify…'
                      : selectedMakeKeys.size > 0 && selectedModelKeys.size > 0
                        ? `Remove ${selectedMakeKeys.size} make${selectedMakeKeys.size !== 1 ? 's' : ''} + ${selectedModelKeys.size} model${selectedModelKeys.size !== 1 ? 's' : ''} & Save`
                        : selectedMakeKeys.size > 0
                          ? `Remove ${selectedMakeKeys.size} make tag${selectedMakeKeys.size !== 1 ? 's' : ''} & Save`
                          : selectedModelKeys.size > 0
                            ? `Remove ${selectedModelKeys.size} model tag${selectedModelKeys.size !== 1 ? 's' : ''} & Save`
                            : 'Select items above to remove'}
                  </button>
                </>
              )}

              {/* ── Hide mode ── */}
              {cleanupMode === 'hide' && (
                <>
                  {hiddenLoading ? (
                    <p className="text-[11px] text-muted-foreground text-center py-1">Loading…</p>
                  ) : (
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      Click any model to toggle its visibility in the storefront vehicle selector. Products keep their tags — the model is just excluded from the dropdown.
                      {hiddenModels.size > 0 && (
                        <> <span className="text-amber-400 font-medium">{hiddenModels.size} model{hiddenModels.size !== 1 ? 's' : ''} hidden.</span></>
                      )}
                    </p>
                  )}
                </>
              )}

              {/* ── Assign Make mode ── */}
              {cleanupMode === 'assign' && (
                <>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    Check the <span className="text-sky-400">make group</span> checkbox to select all products in that group, or check individual models. Then type the correct make name to assign it.
                  </p>
                  <div className="flex gap-2">
                    <input
                      className="flex-1 bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-slate-600 outline-none focus:border-sky-500/50 transition-colors"
                      placeholder="Make name to assign…"
                      value={assignMakeValue}
                      onChange={e => setAssignMakeValue(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') applyAssignMake(); }}
                    />
                    <button
                      onClick={applyAssignMake}
                      disabled={!assignMakeValue.trim() || (selectedModelKeys.size === 0 && selectedMakeKeys.size === 0)}
                      className="px-3 py-2 rounded-lg bg-sky-500/20 hover:bg-sky-500/30 border border-sky-500/30 text-sky-400 text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      Assign
                    </button>
                  </div>
                  {assignMakeValue.trim() && (selectedModelKeys.size > 0 || selectedMakeKeys.size > 0) && (
                    <p className="text-[11px] text-slate-600 leading-relaxed">
                      Sets make → <span className="text-sky-400">"{assignMakeValue.trim()}"</span> on{' '}
                      {[
                        selectedMakeKeys.size > 0 && `all products in ${selectedMakeKeys.size} group${selectedMakeKeys.size !== 1 ? 's' : ''}`,
                        selectedModelKeys.size > 0 && `${selectedModelKeys.size} selected model${selectedModelKeys.size !== 1 ? 's' : ''}`,
                      ].filter(Boolean).join(' + ')}.
                    </p>
                  )}
                </>
              )}

            </div>
          </div>
        </>
      )}

      {/* ── Clear Fitment Confirmation Modal ─────────────────────────────── */}
      {showClearConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowClearConfirm(false)} />
          <div className="relative z-10 w-full max-w-sm bg-card border border-rose-500/30 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
            <div className="flex items-center gap-3 px-6 py-4 border-b border-border/60">
              <Trash2 className="w-5 h-5 text-rose-400 shrink-0" />
              <h2 className="text-base font-semibold text-foreground">Clear All Fitment Data</h2>
            </div>
            <div className="px-6 py-5 flex flex-col gap-4">
              <p className="text-sm text-muted-foreground leading-relaxed">
                This will reset the <span className="text-foreground font-medium">make</span>, <span className="text-foreground font-medium">model</span> and <span className="text-foreground font-medium">year</span> metafields to empty on <span className="text-rose-400 font-medium">every catalogued product</span> in Shopify. This cannot be undone.
              </p>
              <p className="text-xs text-muted-foreground">
                Type <span className="font-mono text-rose-400 font-semibold">CLEAR</span> below to confirm.
              </p>
              <input
                autoFocus
                className="w-full bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-slate-600 outline-none focus:border-rose-500/50 font-mono tracking-widest"
                placeholder="CLEAR"
                value={clearConfirmText}
                onChange={e => setClearConfirmText(e.target.value.toUpperCase())}
                onKeyDown={e => { if (e.key === 'Enter' && clearConfirmText === 'CLEAR') clearFitmentData(); }}
              />
              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => setShowClearConfirm(false)}
                  className="flex-1 px-4 py-2 rounded-lg bg-muted/50 border border-border text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={clearFitmentData}
                  disabled={clearConfirmText !== 'CLEAR'}
                  className="flex-1 px-4 py-2 rounded-lg bg-rose-500/20 hover:bg-rose-500/30 border border-rose-500/30 text-rose-400 text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Clear All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Theme Picker Modal ───────────────────────────────────────────── */}
      {showThemePicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => !themesLoading && setShowThemePicker(false)} />
          <div className="relative z-10 w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden">

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border/60">
              <div className="flex items-center gap-2.5">
                <Car className="w-5 h-5 text-primary" />
                <h2 className="text-base font-semibold text-foreground">Select Theme</h2>
              </div>
              <button onClick={() => setShowThemePicker(false)} disabled={themesLoading} className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-4">
              <p className="text-sm text-muted-foreground mb-4">Choose which theme to push the Vehicle Fitment Filter into.</p>

              {themesLoading ? (
                <div className="flex items-center justify-center py-8 gap-2 text-muted-foreground">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="text-sm">Loading themes…</span>
                </div>
              ) : (
                <div className="flex flex-col gap-2 max-h-64 overflow-y-auto custom-scrollbar">
                  {availableThemes.map(theme => (
                    <button
                      key={theme.id}
                      type="button"
                      onClick={() => setPickedThemeId(theme.id)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-colors ${
                        pickedThemeId === theme.id
                          ? 'bg-primary/10 border-primary/40 text-foreground'
                          : 'bg-muted/30 border-border/60 text-foreground hover:bg-muted/50'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center transition-colors ${
                        pickedThemeId === theme.id ? 'border-primary' : 'border-border'
                      }`}>
                        {pickedThemeId === theme.id && <div className="w-2 h-2 rounded-full bg-primary" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm truncate">{theme.name}</span>
                          {theme.role === 'main' && (
                            <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/25">
                              Live
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-600 mt-0.5">
                          Updated {new Date(theme.updatedAt).toLocaleDateString()}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between gap-2.5 px-6 py-4 border-t border-border/60">
              <button
                onClick={() => setShowThemePicker(false)}
                className="px-4 py-2 rounded-xl bg-muted/50 border border-border text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancel
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => pickedThemeId && pushJsOnly(pickedThemeId)}
                  disabled={!pickedThemeId || themesLoading}
                  title="Push JS files only — CSS, Liquid sections, and settings are not touched"
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-muted/50 hover:bg-muted border border-border text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <FileCode className="w-4 h-4" />
                  Push JS Only
                </button>
                <button
                  onClick={() => pickedThemeId && pushToTheme(pickedThemeId)}
                  disabled={!pickedThemeId || themesLoading}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/20 hover:bg-primary/30 border border-primary/30 text-primary text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Car className="w-4 h-4" />
                  Push to{pickedThemeId ? ` "${availableThemes.find(t => t.id === pickedThemeId)?.name}"` : ' Theme'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── CSV Import Modal ─────────────────────────────────────────────── */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => { if (!importRunning) setShowImportModal(false); }} />
          <div className="relative z-10 w-full max-w-2xl bg-card border border-border rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">

            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border/60">
              <div className="flex items-center gap-2.5">
                <Upload className="w-5 h-5 text-primary" />
                <h2 className="text-base font-semibold text-foreground">Bulk Import Fitment</h2>
              </div>
              <button
                onClick={() => { if (!importRunning) setShowImportModal(false); }}
                disabled={importRunning}
                className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-5">

              {/* Format guide */}
              <div className="bg-primary/5 border border-primary/15 rounded-xl px-4 py-3 text-xs text-muted-foreground space-y-1">
                <p className="font-medium text-foreground">CSV Format</p>
                <p>Required column: <span className="font-mono text-primary">handle</span> — must match the Shopify product handle exactly.</p>
                <p>Optional columns: <span className="font-mono text-foreground">make</span>, <span className="font-mono text-foreground">model</span>, <span className="font-mono text-foreground">year</span> — comma-separated values (e.g. <span className="font-mono">Ford,Toyota</span>).</p>
                <p className="text-muted-foreground">Use <span className="font-medium text-muted-foreground">Export CSV</span> to download your current fitment data as a template.</p>
              </div>

              {/* File picker */}
              {!importDone && (
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wide block mb-2">CSV File</label>
                  <div
                    className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border rounded-xl py-8 px-4 cursor-pointer hover:border-primary/30 hover:bg-primary/5 transition-colors"
                    onClick={() => importFileRef.current?.click()}
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => {
                      e.preventDefault();
                      const file = e.dataTransfer.files[0];
                      if (file) handleImportFile(file);
                    }}
                  >
                    <Upload className="w-8 h-8 text-slate-600" />
                    <p className="text-sm text-muted-foreground">
                      {importFilename
                        ? <span className="text-foreground font-medium">{importFilename}</span>
                        : <>Drop a CSV file here, or <span className="text-primary font-medium">click to browse</span></>
                      }
                    </p>
                    {importFilename && importHeaders.length > 0 && (
                      <p className="text-xs text-slate-600">{importSampleRows.length} preview rows · {importHeaders.length} columns</p>
                    )}
                  </div>
                  <input
                    ref={importFileRef}
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    onChange={e => { const file = e.target.files?.[0]; if (file) handleImportFile(file); e.target.value = ''; }}
                  />
                </div>
              )}

              {/* Column preview table */}
              {importHeaders.length > 0 && !importDone && !importRunning && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Preview (first {importSampleRows.length} rows)</p>
                  <div className="overflow-x-auto rounded-xl border border-border/60">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-border/60 bg-muted/30">
                          {importHeaders.map(h => {
                            const isKey = ['handle','make','model','year'].includes(h.toLowerCase());
                            return (
                              <th key={h} className={`px-3 py-2 text-left font-medium whitespace-nowrap ${isKey ? 'text-primary' : 'text-muted-foreground'}`}>
                                {h}
                                {isKey && <span className="ml-1 text-[9px] text-primary/60">✓</span>}
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {importSampleRows.map((row, i) => (
                          <tr key={i} className={`border-b border-border/50 last:border-0 ${i % 2 === 0 ? '' : 'bg-muted/20'}`}>
                            {importHeaders.map(h => (
                              <td key={h} className="px-3 py-2 text-muted-foreground max-w-[200px] truncate" title={row[h]}>{row[h] || <span className="text-slate-700">—</span>}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {!importHeaders.some(h => h.toLowerCase() === 'handle') && (
                    <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-400">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                      No <span className="font-mono font-medium">handle</span> column found. Import will fail without it.
                    </p>
                  )}
                </div>
              )}

              {/* Running progress */}
              {importRunning && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span className="flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin text-primary" /> Importing fitment data…</span>
                    <span>{importProgress.current} / {importProgress.total}</span>
                  </div>
                  <div className="h-2 bg-muted/50 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full transition-all duration-300"
                      style={{ width: importProgress.total > 0 ? `${Math.round((importProgress.current / importProgress.total) * 100)}%` : '0%' }}
                    />
                  </div>
                </div>
              )}

              {/* Error */}
              {importError && (
                <div className="flex items-start gap-2.5 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-xl text-sm text-red-400">
                  <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{importError}</span>
                </div>
              )}

              {/* Done result */}
              {importDone && (
                <div className="space-y-3">
                  <div className="flex items-start gap-2.5 px-4 py-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-sm text-emerald-400">
                    <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                    <div className="space-y-0.5">
                      <p className="font-medium">Import complete</p>
                      <p className="text-xs text-emerald-600">
                        {importDone.total} row{importDone.total !== 1 ? 's' : ''} processed
                        {importDone.notFound > 0 && ` · ${importDone.notFound} handle${importDone.notFound !== 1 ? 's' : ''} not found in Shopify`}
                        {importDone.errors > 0 && ` · ${importDone.errors} error${importDone.errors !== 1 ? 's' : ''}`}
                      </p>
                    </div>
                  </div>
                  {importDone.notFoundHandles.length > 0 && (
                    <div className="bg-amber-500/5 border border-amber-500/15 rounded-xl px-4 py-3 text-xs text-amber-400 space-y-1">
                      <p className="font-medium">Handles not found in Shopify:</p>
                      <p className="font-mono text-amber-500/70 leading-relaxed">
                        {importDone.notFoundHandles.join(', ')}
                        {importDone.notFound > importDone.notFoundHandles.length && ` … +${importDone.notFound - importDone.notFoundHandles.length} more`}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal footer */}
            <div className="px-6 py-4 border-t border-border/60 flex items-center justify-between gap-3">
              {importDone ? (
                <>
                  <p className="text-xs text-slate-600">Products list was refreshed automatically.</p>
                  <button
                    onClick={() => setShowImportModal(false)}
                    className="px-5 py-2 rounded-xl bg-primary/20 hover:bg-primary/30 border border-primary/30 text-primary text-sm font-medium transition-all"
                  >
                    Done
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setShowImportModal(false)}
                    disabled={importRunning}
                    className="px-4 py-2 rounded-xl bg-muted/50 hover:bg-muted border border-border text-muted-foreground text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={runImport}
                    disabled={!importCsvText || importRunning || !importHeaders.some(h => h.toLowerCase() === 'handle')}
                    className="flex items-center gap-2 px-5 py-2 rounded-xl bg-primary/20 hover:bg-primary/30 border border-primary/30 text-primary text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {importRunning
                      ? <><Loader2 className="w-4 h-4 animate-spin" /> Importing…</>
                      : <><Upload className="w-4 h-4" /> Run Import</>
                    }
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

    </Layout>
  );
}
